/**
 * Element drawing utilities for floor plan rendering
 */

import { apply3DRotationTo2D, calculateMiterPoint } from './geometryUtils';
import { isElectricalDeviceObject } from './electricalDevices';
import { enterScreenSpace } from './canvasUtils';
import { lightSymbolOf } from './lightingClassification';
import { isDemoedOpening } from './openingSyncUtils';
import { isDemoed } from './demoScope';
import { RAILING_COLOR, isRailingWall } from '../constants/inferredStyle';
import {
  DEMOED_HATCH_COLOR,
  DEMOED_HATCH_COLOR_EMPHASIS,
} from '../constants/demoedStyle';
import { calculateWallFootprint } from '@shared-lib/geometry/wallGeometry';
import { wallContextFadeStops } from './wallContextFade';
import { chooseRoomLabelAnchors } from '@shared-lib/geometry/roomLabelAnchor';
import { isSatelliteRoom } from './roomGroupUtils';
import { roomAnchorLabelText } from './roomLabelText';
import {
  drawLabel,
  getRoomType,
  filterObjectsByHeight,
  findFloorY,
} from './floorPlanUtils';
import { isPointNearDoor, drawDoors, computeDoorLeaves } from './doorUtils';
import {
  getObjectType,
  getObjectColor,
  drawObjectShape,
} from './objectDrawingUtils';
import { HIGHLIGHT_COLORS, ELEMENT_COLORS } from '../constants/highlightColors';
import { isBathroomFixtureType } from '../constants/bathroomFixtureConstants';
import { isSink, getSinkType, findSinkParentCountertop } from './sinkHelpers';
import { isKitchenSink } from '@shared-lib/sink-types';
import {
  isFrenchDoor,
  isSlidingDoor,
  isPocketDoor,
  isInswing,
  getSwingDoorConfig,
  DOOR_TYPE_DISPLAY_NAMES,
} from '../constants/doorTypes';

/**
 * Match an opening render entry against the selectedItem accounting for
 * the slim / canonical id split. Post-PR #634, hit-testing returns the
 * resolved opening keyed by the slim room copy's id, while the render
 * arrays in single-room view (and post-dedup elsewhere) are the
 * canonical structure-level entries keyed by the structure id. A naive
 * `selectedItem.id === item.id` match misses across that boundary and
 * no highlight fires. Check both directions of the FK so the highlight
 * lights up regardless of which side of the slim / canonical split
 * each one came from.
 */
export const matchesOpeningSelection = (item, selectedItem) => {
  if (!selectedItem || !item || !item.id) return false;
  if (selectedItem.id === item.id) return true;
  if (
    selectedItem.structureOpeningId &&
    selectedItem.structureOpeningId === item.id
  )
    return true;
  if (item.structureOpeningId && item.structureOpeningId === selectedItem.id)
    return true;
  return false;
};

/**
 * Generates a random color for walls based on ID
 * @param {string} wallId - Wall identifier for consistent colors
 * @returns {string} HSL color string
 */
export const getRandomWallColor = (wallId) => {
  // Use wall ID as seed for consistent colors
  let hash = 0;
  const str = wallId || Math.random().toString();
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  // Generate pastel colors for better visibility
  const hue = Math.abs(hash) % 360;
  const saturation = 60 + (Math.abs(hash >> 8) % 30); // 60-90%
  const lightness = 70 + (Math.abs(hash >> 16) % 20); // 70-90%

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * Draw diagonal hatch pattern for demoed wall sections
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Array} corners - Array of 4 corner points [[x, z], ...]
 * @param {number} scale - Current scale factor
 * @param {boolean} isSelected - Whether the element is selected
 */
const drawDemoedWallHatch = (ctx, corners, scale, isSelected = false) => {
  if (!corners || corners.length < 4) return;

  ctx.save();

  // Create clipping path from corners
  ctx.beginPath();
  ctx.moveTo(corners[0][0], corners[0][1]);
  for (let i = 1; i < corners.length; i++) {
    ctx.lineTo(corners[i][0], corners[i][1]);
  }
  ctx.closePath();
  ctx.clip();

  // Calculate bounding box
  const minX = Math.min(...corners.map((c) => c[0]));
  const maxX = Math.max(...corners.map((c) => c[0]));
  const minY = Math.min(...corners.map((c) => c[1]));
  const maxY = Math.max(...corners.map((c) => c[1]));

  // Draw diagonal lines at 45 degrees
  const spacing = 0.04; // 4cm spacing in world units
  const color = isSelected ? DEMOED_HATCH_COLOR_EMPHASIS : DEMOED_HATCH_COLOR; // Red for demo indication
  const lineWidth = Math.max(0.003, 1.5 / scale); // Thin lines

  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;

  // Draw lines from bottom-left to top-right direction
  const diagonalLength = maxX - minX + (maxY - minY);
  for (
    let offset = -diagonalLength;
    offset < diagonalLength;
    offset += spacing
  ) {
    const startX = minX + offset;
    const startY = maxY;
    const endX = startX + diagonalLength;
    const endY = startY - diagonalLength;

    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();
  }

  ctx.restore();
};

/**
 * Draw the structure's DEMOLISHED walls (the `structure.demolished` bucket) as
 * red-hatched "ghost" rectangles.
 *
 * Under the demolition-bucket model a torn-out partition is REMOVED from the
 * active walls, so the normal demoed-wall path in `drawWalls` — which hatches a
 * gap in a still-present wall — never fires for it. This standalone pass redraws
 * each bucket wall as a hatched rectangle when the `showDemoedWalls` toggle is
 * ON, reusing the shared demoed hatch styling so it matches a manually demoed
 * wall. Toggle OFF → nothing (the active arrays already read as open floor).
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object} demolished  `structure.demolished` = { walls, ... }
 * @param {number} scale
 * @param {Object} selectedItem  current selection (drives emphasis colour)
 * @param {boolean} showDemoedWalls
 */
export const drawDemolishedGhosts = (
  ctx,
  demolished,
  scale,
  selectedItem,
  showDemoedWalls
) => {
  if (!showDemoedWalls || !demolished) return;
  (demolished.walls || []).forEach((wall) => {
    if (!wall.center || !wall.dimensions || !wall.rotation) return;

    const halfLength = (wall.dimensions.x || 0) / 2;
    const halfThick = (wall.dimensions.z || 0.1143) / 2; // default 4.5"
    const dir = apply3DRotationTo2D(wall.rotation);
    const perp = [-dir[1], dir[0]];
    const start = [
      wall.center.x - dir[0] * halfLength,
      wall.center.z - dir[1] * halfLength,
    ];
    const end = [
      wall.center.x + dir[0] * halfLength,
      wall.center.z + dir[1] * halfLength,
    ];
    const corners = [
      [start[0] - perp[0] * halfThick, start[1] - perp[1] * halfThick],
      [end[0] - perp[0] * halfThick, end[1] - perp[1] * halfThick],
      [end[0] + perp[0] * halfThick, end[1] + perp[1] * halfThick],
      [start[0] + perp[0] * halfThick, start[1] + perp[1] * halfThick],
    ];

    const isSelected = !!selectedItem && selectedItem.id === wall.id;

    // Light-red fill + diagonal hatch — same look as a demoed-wall gap.
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(corners[0][0], corners[0][1]);
    for (let i = 1; i < corners.length; i++) {
      ctx.lineTo(corners[i][0], corners[i][1]);
    }
    ctx.closePath();
    ctx.fillStyle = isSelected ? '#FFCCCC' : '#FFE0E0';
    ctx.fill();
    ctx.restore();
    drawDemoedWallHatch(ctx, corners, scale, isSelected);
  });
};

/**
 * Check if a wall segment intersects with any windows
 * @param {Object} wall - Wall object
 * @param {Array} windows - Array of window objects
 * @returns {Array} Array of window objects that intersect with this wall
 */
// Check if an opening geometrically overlaps a wall's span
export const isOpeningWithinWallSpan = (opening, wall) => {
  if (!opening.center || !wall.center || !wall.dimensions) return false;
  const wallDir = apply3DRotationTo2D(wall.rotation);
  const wallToOpening = [
    opening.center.x - wall.center.x,
    opening.center.z - wall.center.z,
  ];

  // Reject openings that are far from the wall line (perpendicular distance).
  // This prevents matching openings to end-cap room walls that are
  // perpendicular to the main wall face the opening sits on.
  const perpDir = [-wallDir[1], wallDir[0]];
  const perpDist = Math.abs(
    wallToOpening[0] * perpDir[0] + wallToOpening[1] * perpDir[1]
  );
  if (perpDist > 0.3) return false;

  const proj = wallToOpening[0] * wallDir[0] + wallToOpening[1] * wallDir[1];
  const halfWall = wall.dimensions.x / 2;
  const halfOpening = (opening.dimensions?.x || 0.8) / 2;
  return proj + halfOpening >= -halfWall && proj - halfOpening <= halfWall;
};

const getWindowsOnWall = (wall, windows) => {
  if (!windows || windows.length === 0) return [];

  return windows.filter((window) => {
    // Reject unhydrated slim refs — drawWalls' opening-gap loop reads
    // window.dimensions.x downstream, so a slim that slipped past the
    // center check (e.g. a slim with a stray center field written by a
    // pre-fix opening update) would crash on the next access.
    if (!window?.center || !window?.dimensions) return false;
    // Check if this window belongs to this wall via parentWallIds
    const parentId = window.parentWallIds?.[0];
    if (parentId && wall.id) {
      if (parentId === wall.id) return true;
      // Also match if parentId references this wall's associated structure wall,
      // but only if the opening geometrically overlaps this wall segment
      if (parentId === wall.alignment?.structureWallId) {
        return isOpeningWithinWallSpan(window, wall);
      }
      return false;
    }

    // Fallback to geometric check if parent ID is not available
    let wallDir = apply3DRotationTo2D(wall.rotation);
    const wallNormal = [-wallDir[1], wallDir[0]];

    // Check if window center is close to wall line
    const wallToWindow = [
      window.center.x - wall.center.x,
      window.center.z - wall.center.z,
    ];

    // Project window center onto wall normal
    const distToWall = Math.abs(
      wallToWindow[0] * wallNormal[0] + wallToWindow[1] * wallNormal[1]
    );

    // Check if window is on this wall (within 20cm tolerance)
    if (distToWall > 0.2) return false;

    // Check if window is within wall bounds
    const projectionOnWall =
      wallToWindow[0] * wallDir[0] + wallToWindow[1] * wallDir[1];

    const halfWallLength = wall.dimensions.x / 2;
    return (
      Math.abs(projectionOnWall) <= halfWallLength + window.dimensions.x / 2
    );
  });
};

/**
 * Helper function to get openings that belong to a wall
 * @param {Object} wall - Wall object
 * @param {Array} openings - Array of all opening objects
 * @returns {Array} Array of opening objects that intersect with this wall
 */
const getOpeningsOnWall = (wall, openings) => {
  if (!openings || openings.length === 0) return [];

  return openings.filter((opening) => {
    if (!opening?.center || !opening?.dimensions) return false;
    // Check if this opening belongs to this wall via parentWallIds
    const parentId = opening.parentWallIds?.[0];
    if (parentId && wall.id) {
      if (parentId === wall.id) return true;
      if (parentId === wall.alignment?.structureWallId) {
        return isOpeningWithinWallSpan(opening, wall);
      }
      return false;
    }

    // Fallback to geometric check if parent ID is not available
    if (
      !opening.center ||
      opening.center.x === undefined ||
      opening.center.z === undefined
    ) {
      return false;
    }

    // Use consistent rotation method for all walls
    let wallDir = apply3DRotationTo2D(wall.rotation);
    const wallNormal = [-wallDir[1], wallDir[0]];

    // Check if opening center is close to wall line
    const wallToOpening = [
      opening.center.x - wall.center.x,
      opening.center.z - wall.center.z,
    ];

    // Project opening center onto wall normal
    const distToWall = Math.abs(
      wallToOpening[0] * wallNormal[0] + wallToOpening[1] * wallNormal[1]
    );

    // Check if opening is on this wall (within 20cm tolerance)
    if (distToWall > 0.2) return false;

    // Check if opening is within wall bounds
    const projectionOnWall =
      wallToOpening[0] * wallDir[0] + wallToOpening[1] * wallDir[1];

    const halfWallLength = wall.dimensions.x / 2;
    const halfOpeningWidth = (opening.dimensions?.x || 0.8) / 2;

    return (
      projectionOnWall + halfOpeningWidth >= -halfWallLength &&
      projectionOnWall - halfOpeningWidth <= halfWallLength
    );
  });
};

/**
 * Objects that sit geometrically on a wall (sibling of getOpeningsOnWall for
 * room.objects[], which don't carry parentWallIds). Uses isOpeningWithinWallSpan
 * (works against any center/dimensions/rotation span) so it rejects far-off
 * objects (perp dist) and keeps those overlapping the wall run.
 */
export const getObjectsOnWall = (wall, objects) => {
  if (!objects || objects.length === 0) return [];
  if (!wall?.center || !wall?.dimensions) return [];
  return objects.filter((obj) => {
    if (!obj?.center || !obj?.dimensions) return false;
    return isOpeningWithinWallSpan(obj, wall);
  });
};

/**
 * Check if a wall segment intersects with any doors
 * @param {Object} wall - Wall object
 * @param {Array} doors - Array of door objects
 * @returns {Array} Array of door objects that intersect with this wall
 */
const getDoorsOnWall = (wall, doors) => {
  if (!doors || doors.length === 0) return [];

  return doors.filter((door) => {
    if (!door?.center || !door?.dimensions) return false;
    // Check if this door belongs to this wall via parentWallIds
    const parentId = door.parentWallIds?.[0];
    if (parentId && wall.id) {
      if (parentId === wall.id) return true;
      // Also match if parentId references this wall's associated structure wall,
      // but only if the opening geometrically overlaps this wall segment
      if (parentId === wall.alignment?.structureWallId) {
        return isOpeningWithinWallSpan(door, wall);
      }
      return false;
    }

    // Fallback to geometric check if parent ID is not available
    if (
      !door.center ||
      door.center.x === undefined ||
      door.center.z === undefined
    ) {
      return false;
    }

    // Use consistent rotation method for all walls
    let wallDir = apply3DRotationTo2D(wall.rotation);
    const wallNormal = [-wallDir[1], wallDir[0]];

    // Check if door center is close to wall line
    const wallToDoor = [
      door.center.x - wall.center.x,
      door.center.z - wall.center.z,
    ];

    // Project door center onto wall normal
    const distToWall = Math.abs(
      wallToDoor[0] * wallNormal[0] + wallToDoor[1] * wallNormal[1]
    );

    // Check if door is on this wall (within 30cm tolerance - increased for doors)
    if (distToWall > 0.3) return false;

    // Check if door is within wall bounds
    const projectionOnWall =
      wallToDoor[0] * wallDir[0] + wallToDoor[1] * wallDir[1];

    const halfWallLength = wall.dimensions.x / 2;
    const halfDoorWidth = door.dimensions.x / 2;

    return Math.abs(projectionOnWall) <= halfWallLength + halfDoorWidth;
  });
};

/**
 * Calculate the intersection point of two lines
 * @param {Array} p1 - First point of line 1
 * @param {Array} d1 - Direction vector of line 1
 * @param {Array} p2 - First point of line 2
 * @param {Array} d2 - Direction vector of line 2
 * @returns {Array|null} Intersection point or null if parallel
 */
const lineIntersection = (p1, d1, p2, d2) => {
  const cross = d1[0] * d2[1] - d1[1] * d2[0];
  if (Math.abs(cross) < 0.001) return null; // Parallel lines

  const dx = p2[0] - p1[0];
  const dy = p2[1] - p1[1];
  const t1 = (dx * d2[1] - dy * d2[0]) / cross;

  return [p1[0] + t1 * d1[0], p1[1] + t1 * d1[1]];
};

/**
 * Create a ray from a point in a given direction
 */
const createRay = (point, direction) => ({
  origin: [...point],
  direction: [...direction],
});

/**
 * Find intersection of two rays
 */
const rayIntersection = (ray1, ray2) => {
  const [x1, y1] = ray1.origin;
  const [dx1, dy1] = ray1.direction;
  const [x2, y2] = ray2.origin;
  const [dx2, dy2] = ray2.direction;

  const cross = dx1 * dy2 - dy1 * dx2;
  if (Math.abs(cross) < 0.0001) return null; // Parallel rays

  const t1 = ((x2 - x1) * dy2 - (y2 - y1) * dx2) / cross;

  // For ray casting walls, we allow extension in both directions
  return [x1 + t1 * dx1, y1 + t1 * dy1];
};

/**
 * Draws walls with proper thickness and coloring using ray casting
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Array} walls - Array of wall objects
 * @param {number} scale - Current zoom scale
 * @param {Object} selectedItem - Currently selected item
 * @param {boolean} debugMode - Whether in debug mode
 * @param {Array} rooms - Array of room objects
 * @param {Array} windows - Array of window objects
 * @param {Array} doors - Array of door objects
 * @param {Array} openings - Array of opening objects
 * @param {Object} wallExtensionMetadata - Metadata about wall extensions
 * @param {boolean} showRoomWallsBold - When true, colors zero thickness room walls in black
 * @param {Array} structureWalls - Array of structure-level walls for drawing associated walls
 * @returns {Array} Wall normal data for drawing arrows
 */
/**
 * Draw nearby structure walls from OTHER rooms as faint context in the
 * single-room view, faded by their distance to the selected room's floor
 * polygon (solid out to ~1 ft, gone by ~2 ft). Each wall's footprint is filled
 * with a gradient along its axis using `wallContextFadeStops`, matching the
 * gray of the selected room's own faded structure walls. No openings are
 * carved — these are pure context.
 *
 * @param {CanvasRenderingContext2D} ctx - in world-space transform
 * @param {Array} contextWalls - structure walls NOT belonging to the selected room
 * @param {Array<{x,z}>} roomPolygon - selected room's floor polygon corners
 * @param {number} scale - canvas scale (for stroke width)
 */
export const drawContextWalls = (ctx, contextWalls, roomPolygon, scale) => {
  if (!contextWalls?.length || !roomPolygon || roomPolygon.length < 2) return;

  ctx.save();
  for (const wall of contextWalls) {
    const fsd = wall.frontFacesSameDirection !== false;
    const fp = calculateWallFootprint(wall, fsd);
    if (!fp) continue;

    // Centerline endpoints (front/back midpoints) for distance sampling + the
    // gradient axis.
    const leftEnd = {
      x: (fp.frontLeft.x + fp.backLeft.x) / 2,
      z: (fp.frontLeft.z + fp.backLeft.z) / 2,
    };
    const rightEnd = {
      x: (fp.frontRight.x + fp.backRight.x) / 2,
      z: (fp.frontRight.z + fp.backRight.z) / 2,
    };

    const { visible, stops } = wallContextFadeStops(
      leftEnd,
      rightEnd,
      roomPolygon
    );
    if (!visible) continue;

    const fillGrad = ctx.createLinearGradient(
      leftEnd.x,
      leftEnd.z,
      rightEnd.x,
      rightEnd.z
    );
    const strokeGrad = ctx.createLinearGradient(
      leftEnd.x,
      leftEnd.z,
      rightEnd.x,
      rightEnd.z
    );
    for (const s of stops) {
      fillGrad.addColorStop(s.t, `rgba(188,188,188,${s.alpha})`);
      strokeGrad.addColorStop(s.t, `rgba(169,169,169,${s.alpha})`);
    }

    ctx.beginPath();
    ctx.moveTo(fp.frontLeft.x, fp.frontLeft.z);
    ctx.lineTo(fp.frontRight.x, fp.frontRight.z);
    ctx.lineTo(fp.backRight.x, fp.backRight.z);
    ctx.lineTo(fp.backLeft.x, fp.backLeft.z);
    ctx.closePath();
    ctx.fillStyle = fillGrad;
    ctx.fill();
    ctx.lineWidth = 1 / scale;
    ctx.strokeStyle = strokeGrad;
    ctx.stroke();
  }
  ctx.restore();
};

export const drawWalls = (
  ctx,
  walls,
  scale,
  selectedItem,
  debugMode = false,
  rooms = [],
  windows = [],
  doors = [],
  openings = [],
  wallExtensionMetadata = {},
  showRoomWallsBold = false,
  structureWalls = [],
  showDemoedWalls = true,
  // Trim/fade the portion of a structure wall that extends past its
  // associated room walls. This is meaningful only in single-room view
  // (the wall continues into adjacent, hidden rooms). In the all-rooms
  // view every wall should render solid, so callers pass false there.
  isSingleRoomView = true
) => {
  // Get canvas center for coordinate transformation
  const canvas = ctx.canvas;
  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  // Store wall data for drawing normals later
  const wallNormals = [];

  // Pre-pass: Draw associated structure walls in light grey when showRoomWallsBold is on
  if (showRoomWallsBold && structureWalls && structureWalls.length > 0) {
    // Collect unique structure wall IDs that are associated with the room walls being drawn
    const structureWallIdsToDrawSet = new Set();
    walls.forEach((wall) => {
      const actualThickness = wall.dimensions?.z;
      const isZeroThicknessRoomWall =
        !actualThickness && !wall._isStructureLevel;
      if (isZeroThicknessRoomWall && wall.alignment?.structureWallId) {
        structureWallIdsToDrawSet.add(wall.alignment.structureWallId);
      }
    });

    // Also include user-created structure walls (they may not have room walls if placed outside rooms)
    structureWalls.forEach((sw) => {
      if (sw.isUserCreated) {
        structureWallIdsToDrawSet.add(sw.id);
      }
    });

    // For scanned walls: include structure walls whose IDs match room wall IDs
    // (scanned walls share the same ID between room wall and structure wall)
    walls.forEach((wall) => {
      if (structureWalls.some((sw) => sw.id === wall.id)) {
        structureWallIdsToDrawSet.add(wall.id);
      }
    });

    // Check if selected item is a structure wall or a room wall with a structure wall reference
    const selectedStructureWallId =
      selectedItem?.alignment?.structureWallId || // Room wall referencing structure wall
      (selectedItem?._isStructureWall ? selectedItem?.id : null) || // Directly selected structure wall
      (structureWalls?.some((sw) => sw.id === selectedItem?.id)
        ? selectedItem?.id
        : null); // Scanned wall with shared ID

    // Draw each associated structure wall
    structureWallIdsToDrawSet.forEach((structureWallId) => {
      const structureWall = structureWalls.find(
        (sw) => sw.id === structureWallId
      );
      if (!structureWall || !structureWall.center || !structureWall.dimensions)
        return;

      const isSelectedStructureWall =
        structureWallId === selectedStructureWallId;

      // Calculate structure wall geometry
      const swHalfLength = structureWall.dimensions.x / 2;
      const swThickness = structureWall.dimensions.z || 0.1143; // Default 4.5 inches
      const swHalfThick = swThickness / 2;

      let swWallDir = apply3DRotationTo2D(structureWall.rotation);
      const swPerpDir = [-swWallDir[1], swWallDir[0]];

      const swStart = [
        structureWall.center.x - swWallDir[0] * swHalfLength,
        structureWall.center.z - swWallDir[1] * swHalfLength,
      ];
      const swEnd = [
        structureWall.center.x + swWallDir[0] * swHalfLength,
        structureWall.center.z + swWallDir[1] * swHalfLength,
      ];

      // Calculate corners (symmetric thickness). Used as the parametric
      // basis for opening-gap rendering below — keep these as the
      // unmitered parallelogram so gap interpolation by ratio still
      // lands at the same wall-direction position on both faces.
      const swCorners = [
        [
          swStart[0] - swPerpDir[0] * swHalfThick,
          swStart[1] - swPerpDir[1] * swHalfThick,
        ],
        [
          swEnd[0] - swPerpDir[0] * swHalfThick,
          swEnd[1] - swPerpDir[1] * swHalfThick,
        ],
        [
          swEnd[0] + swPerpDir[0] * swHalfThick,
          swEnd[1] + swPerpDir[1] * swHalfThick,
        ],
        [
          swStart[0] + swPerpDir[0] * swHalfThick,
          swStart[1] + swPerpDir[1] * swHalfThick,
        ],
      ];

      // Wall outline: use the mitered trapezoidal footprint when miters
      // are non-zero. Falls back to swCorners (the parallelogram) for
      // unmitered walls.
      const fsd = structureWall.frontFacesSameDirection !== false;
      const fp = calculateWallFootprint(structureWall, fsd);
      const fullOutline = fp
        ? [
            [fp.frontLeft.x, fp.frontLeft.z],
            [fp.frontRight.x, fp.frontRight.z],
            [fp.backRight.x, fp.backRight.z],
            [fp.backLeft.x, fp.backLeft.z],
          ]
        : swCorners;

      // When the room walls drawn alongside this structure wall don't span
      // its full length (e.g. only one room is selected and the structure
      // wall continues into adjacent rooms), trim and fade the visible
      // portion so the wall ends ~1' beyond the room.
      let roomMinProj = Infinity;
      let roomMaxProj = -Infinity;
      walls.forEach((wall) => {
        if (!wall || !wall.center || !wall.dimensions) return;
        const associated =
          wall.alignment?.structureWallId === structureWall.id ||
          wall.id === structureWall.id;
        if (!associated) return;
        let wDir;
        if (wall.alignment?.bottomLeft && wall.alignment?.bottomRight) {
          const dx = wall.alignment.bottomRight.x - wall.alignment.bottomLeft.x;
          const dz = wall.alignment.bottomRight.z - wall.alignment.bottomLeft.z;
          const len = Math.sqrt(dx * dx + dz * dz);
          wDir =
            len > 0.001
              ? [dx / len, dz / len]
              : apply3DRotationTo2D(wall.rotation);
        } else {
          wDir = apply3DRotationTo2D(wall.rotation);
        }
        const wHalfLen = wall.dimensions.x / 2;
        const ends = [
          [
            wall.center.x - wDir[0] * wHalfLen,
            wall.center.z - wDir[1] * wHalfLen,
          ],
          [
            wall.center.x + wDir[0] * wHalfLen,
            wall.center.z + wDir[1] * wHalfLen,
          ],
        ];
        ends.forEach(([px, pz]) => {
          const proj =
            (px - structureWall.center.x) * swWallDir[0] +
            (pz - structureWall.center.z) * swWallDir[1];
          if (proj < roomMinProj) roomMinProj = proj;
          if (proj > roomMaxProj) roomMaxProj = proj;
        });
      });

      // Solid extension (full alpha) past room wall ends, then fade band.
      const FADE_INNER = 0.3048; // 1 ft solid extension past room wall
      const FADE_OUTER = 0.6096; // 2 ft total before fully invisible
      // Snap the room range to the structure wall end when it's within
      // ~one wall thickness, so corner mitering doesn't trigger a fade
      // inside an actual room corner.
      const CORNER_SNAP = Math.max(swThickness * 1.5, 0.15);
      const hasAssociatedRoomWalls =
        isFinite(roomMinProj) && isFinite(roomMaxProj);
      const adjRoomMin =
        hasAssociatedRoomWalls && roomMinProj - -swHalfLength < CORNER_SNAP
          ? -swHalfLength
          : roomMinProj;
      const adjRoomMax =
        hasAssociatedRoomWalls && swHalfLength - roomMaxProj < CORNER_SNAP
          ? swHalfLength
          : roomMaxProj;
      const trimMin = hasAssociatedRoomWalls
        ? Math.max(-swHalfLength, adjRoomMin - FADE_OUTER)
        : -swHalfLength;
      const trimMax = hasAssociatedRoomWalls
        ? Math.min(swHalfLength, adjRoomMax + FADE_OUTER)
        : swHalfLength;
      const innerLeft = hasAssociatedRoomWalls
        ? Math.max(trimMin, adjRoomMin - FADE_INNER)
        : -swHalfLength;
      const innerRight = hasAssociatedRoomWalls
        ? Math.min(trimMax, adjRoomMax + FADE_INNER)
        : swHalfLength;
      // Skip trim/fade while this structure wall is being actively dragged
      // by an endpoint handle. During a live drag the structure wall's
      // dimensions update on every pointer move, but its associated room
      // walls aren't re-projected (rewrapRoomWall) until pointer-up — so
      // their projections are stale and would fade the wall down to its
      // pre-drag length.
      const isThisStructureWallBeingDragged =
        selectedItem?.isDragging === true &&
        selectedItem?.id === structureWall.id;
      const shouldTrim =
        isSingleRoomView &&
        !isThisStructureWallBeingDragged &&
        hasAssociatedRoomWalls &&
        (trimMin > -swHalfLength + 0.001 || trimMax < swHalfLength - 0.001) &&
        trimMax - trimMin > 0.001;

      let outline = fullOutline;
      // Selection uses the shared element highlight, not a private pink/red
      // pair. This block had its own, so a selected structure wall came up red
      // while every other element you click comes up magenta — the same state,
      // two colours, for no reason anyone could see.
      let fillStyle = isSelectedStructureWall
        ? HIGHLIGHT_COLORS.PRIMARY_FILL
        : '#BCBCBC';
      let strokeStyle = isSelectedStructureWall
        ? HIGHLIGHT_COLORS.PRIMARY
        : '#A9A9A9';

      if (shouldTrim) {
        // Build axis-aligned trimmed parallelogram by lerping along swCorners.
        const ratioMin = (trimMin + swHalfLength) / (swHalfLength * 2);
        const ratioMax = (trimMax + swHalfLength) / (swHalfLength * 2);
        const lerp = (a, b, t) => [
          a[0] + (b[0] - a[0]) * t,
          a[1] + (b[1] - a[1]) * t,
        ];
        outline = [
          lerp(swCorners[0], swCorners[1], ratioMin),
          lerp(swCorners[0], swCorners[1], ratioMax),
          lerp(swCorners[3], swCorners[2], ratioMax),
          lerp(swCorners[3], swCorners[2], ratioMin),
        ];

        // Linear gradient along wall axis so trim ends fade to fully transparent.
        const gradStart = [
          structureWall.center.x + swWallDir[0] * trimMin,
          structureWall.center.z + swWallDir[1] * trimMin,
        ];
        const gradEnd = [
          structureWall.center.x + swWallDir[0] * trimMax,
          structureWall.center.z + swWallDir[1] * trimMax,
        ];
        const span = trimMax - trimMin;
        const stop1 = Math.max(0, Math.min(1, (innerLeft - trimMin) / span));
        const stop2 = Math.max(0, Math.min(1, (innerRight - trimMin) / span));
        const alphaStart = innerLeft - trimMin > 0.001 ? 0 : 1;
        const alphaEnd = trimMax - innerRight > 0.001 ? 0 : 1;

        const fillBase = isSelectedStructureWall
          ? '255,182,193'
          : '188,188,188';
        const strokeBase = isSelectedStructureWall
          ? '255,107,107'
          : '169,169,169';

        const fillGrad = ctx.createLinearGradient(
          gradStart[0],
          gradStart[1],
          gradEnd[0],
          gradEnd[1]
        );
        fillGrad.addColorStop(0, `rgba(${fillBase},${alphaStart})`);
        fillGrad.addColorStop(stop1, `rgba(${fillBase},1)`);
        fillGrad.addColorStop(stop2, `rgba(${fillBase},1)`);
        fillGrad.addColorStop(1, `rgba(${fillBase},${alphaEnd})`);

        const strokeGrad = ctx.createLinearGradient(
          gradStart[0],
          gradStart[1],
          gradEnd[0],
          gradEnd[1]
        );
        strokeGrad.addColorStop(0, `rgba(${strokeBase},${alphaStart})`);
        strokeGrad.addColorStop(stop1, `rgba(${strokeBase},1)`);
        strokeGrad.addColorStop(stop2, `rgba(${strokeBase},1)`);
        strokeGrad.addColorStop(1, `rgba(${strokeBase},${alphaEnd})`);

        fillStyle = fillGrad;
        strokeStyle = strokeGrad;
      }

      // Draw the structure wall
      ctx.beginPath();
      ctx.moveTo(outline[0][0], outline[0][1]);
      for (let i = 1; i < outline.length; i++) {
        ctx.lineTo(outline[i][0], outline[i][1]);
      }
      ctx.closePath();

      ctx.fillStyle = fillStyle;
      ctx.fill();

      ctx.strokeStyle = strokeStyle;
      ctx.lineWidth = Math.max(0.02, 2 / scale);
      ctx.stroke();

      // Draw lighter fill in opening gaps so doors/windows/openings are
      // visually distinct from the solid structure wall.
      // Match openings that reference this structure wall directly OR
      // reference a room wall aligned to this structure wall.
      const isOnStructureWall = (opening) => {
        if (!opening?.center) return false;
        const parentId = opening.parentWallIds?.[0];
        if (!parentId) return false;
        if (parentId === structureWall.id) return true;
        const parentRoomWall = walls.find((w) => w.id === parentId);
        if (parentRoomWall?.alignment?.structureWallId === structureWall.id) {
          return isOpeningWithinWallSpan(opening, structureWall);
        }
        return false;
      };
      const allOpenings = [
        ...doors.filter(isOnStructureWall),
        ...windows.filter(isOnStructureWall),
        ...openings.filter(isOnStructureWall),
      ];

      if (allOpenings.length > 0) {
        allOpenings.forEach((opening) => {
          const halfW = (opening.dimensions?.x || 0.8) / 2;
          const wallToOpening = [
            opening.center.x - structureWall.center.x,
            opening.center.z - structureWall.center.z,
          ];
          const proj =
            wallToOpening[0] * swWallDir[0] + wallToOpening[1] * swWallDir[1];

          // Skip gaps that fall outside the trimmed/visible portion of the wall.
          if (
            shouldTrim &&
            (proj + halfW < trimMin || proj - halfW > trimMax)
          ) {
            return;
          }

          const gapStartRatio =
            (proj - halfW + swHalfLength) / (swHalfLength * 2);
          const gapEndRatio =
            (proj + halfW + swHalfLength) / (swHalfLength * 2);

          const gapCorners = [
            [
              swCorners[0][0] +
                (swCorners[1][0] - swCorners[0][0]) * gapStartRatio,
              swCorners[0][1] +
                (swCorners[1][1] - swCorners[0][1]) * gapStartRatio,
            ],
            [
              swCorners[0][0] +
                (swCorners[1][0] - swCorners[0][0]) * gapEndRatio,
              swCorners[0][1] +
                (swCorners[1][1] - swCorners[0][1]) * gapEndRatio,
            ],
            [
              swCorners[3][0] +
                (swCorners[2][0] - swCorners[3][0]) * gapEndRatio,
              swCorners[3][1] +
                (swCorners[2][1] - swCorners[3][1]) * gapEndRatio,
            ],
            [
              swCorners[3][0] +
                (swCorners[2][0] - swCorners[3][0]) * gapStartRatio,
              swCorners[3][1] +
                (swCorners[2][1] - swCorners[3][1]) * gapStartRatio,
            ],
          ];

          // Check if this is a demoed wall (opening subtype)
          const isDemoedWall = isDemoedOpening(opening);

          // Highlight the gap fill when either the parent structure wall is
          // selected (so all openings on that wall flag together) OR this
          // specific opening is the selected item. matchesOpeningSelection
          // bridges the slim room-copy ↔ canonical structure-copy ids.
          const isOpeningSelected = matchesOpeningSelection(
            opening,
            selectedItem
          );
          const showSelectedColor =
            isSelectedStructureWall || isOpeningSelected;

          ctx.beginPath();
          ctx.moveTo(gapCorners[0][0], gapCorners[0][1]);
          for (let i = 1; i < gapCorners.length; i++) {
            ctx.lineTo(gapCorners[i][0], gapCorners[i][1]);
          }
          ctx.closePath();

          if (isDemoedWall) {
            if (showDemoedWalls) {
              // Draw light red background for demoed walls
              ctx.fillStyle = showSelectedColor ? '#FFCCCC' : '#FFE0E0';
              ctx.fill();
              // Draw hatch pattern
              drawDemoedWallHatch(ctx, gapCorners, scale, showSelectedColor);
            }
            // Hidden via the visibility toggle: the demoed span is cleared to
            // open space by drawOpenings, which runs AFTER the main wall loop
            // — so it can also cover the room-wall outline the main loop
            // strokes across this gap. Clearing here (structure pre-pass) only
            // would leave that later black outline behind.
          } else {
            // Regular opening - light grey fill
            ctx.fillStyle = showSelectedColor ? '#FFD6DD' : '#E8E8E8';
            ctx.fill();
          }
        });
      }
    });
  }

  // First pass: collect all wall edge rays
  const wallEdgeRays = [];
  const wallData = [];

  walls.forEach((wall, index) => {
    if (!wall.center || !wall.dimensions || !wall.rotation) {
      console.log(`Wall ${index} missing required properties:`, wall);
      return;
    }

    // Skip structure walls in bold mode - they're already drawn by the pre-pass
    // Only skip if the wall has actual thickness (room walls have zero thickness)
    if (showRoomWallsBold) {
      const actualThickness = wall.dimensions?.z;
      const hasThickness = actualThickness && actualThickness > 0.01; // More than 1cm
      const isStructureWall =
        wall._isStructureWall ||
        (hasThickness && structureWalls?.some((sw) => sw.id === wall.id));
      if (isStructureWall) {
        return; // Already drawn in pre-pass
      }
    }

    // Check for invalid dimensions
    if (wall.dimensions.x <= 0) {
      console.log(`Wall ${index} has invalid length:`, wall.dimensions.x, wall);
      return;
    }

    const halfLength = wall.dimensions.x / 2;
    const actualThickness = wall.dimensions.z;
    // Use thin lines for debug mode or when showRoomWallsBold is on (for zero thickness room walls)
    const useThinLines =
      debugMode ||
      (showRoomWallsBold && !actualThickness && !wall._isStructureLevel);
    const wallThickness = useThinLines
      ? actualThickness || 0.0127 // Thin: actual or 0.5 inches
      : actualThickness || 0.1524; // Normal: actual or 6 inches

    // Compute wall direction: prefer alignment corners (accurate for room walls
    // whose rotation is synced from a cardinal-aligned structure wall), then
    // fall back to rotation.
    let wallDir;
    if (wall.alignment?.bottomLeft && wall.alignment?.bottomRight) {
      const dx = wall.alignment.bottomRight.x - wall.alignment.bottomLeft.x;
      const dz = wall.alignment.bottomRight.z - wall.alignment.bottomLeft.z;
      const len = Math.sqrt(dx * dx + dz * dz);
      wallDir =
        len > 0.001 ? [dx / len, dz / len] : apply3DRotationTo2D(wall.rotation);
    } else {
      wallDir = apply3DRotationTo2D(wall.rotation);
    }
    const perpDir = [-wallDir[1], wallDir[0]];

    const start = [
      wall.center.x - wallDir[0] * halfLength,
      wall.center.z - wallDir[1] * halfLength,
    ];
    const end = [
      wall.center.x + wallDir[0] * halfLength,
      wall.center.z + wallDir[1] * halfLength,
    ];

    // Calculate edge points
    let leftStart, leftEnd, rightStart, rightEnd;

    if (!debugMode && !actualThickness && !useThinLines) {
      // Wall extends outward only (normal mode with zero thickness)
      const outwardDir = [-perpDir[0], -perpDir[1]];
      leftStart = start;
      leftEnd = end;
      rightStart = [
        start[0] + outwardDir[0] * wallThickness,
        start[1] + outwardDir[1] * wallThickness,
      ];
      rightEnd = [
        end[0] + outwardDir[0] * wallThickness,
        end[1] + outwardDir[1] * wallThickness,
      ];
    } else {
      // Wall has symmetric thickness
      const halfThick = wallThickness / 2;
      leftStart = [
        start[0] - perpDir[0] * halfThick,
        start[1] - perpDir[1] * halfThick,
      ];
      leftEnd = [
        end[0] - perpDir[0] * halfThick,
        end[1] - perpDir[1] * halfThick,
      ];
      rightStart = [
        start[0] + perpDir[0] * halfThick,
        start[1] + perpDir[1] * halfThick,
      ];
      rightEnd = [
        end[0] + perpDir[0] * halfThick,
        end[1] + perpDir[1] * halfThick,
      ];
    }

    // Create rays for both edges extending in both directions
    const leftEdgeForward = createRay(leftEnd, wallDir);
    const leftEdgeBackward = createRay(leftStart, [-wallDir[0], -wallDir[1]]);
    const rightEdgeForward = createRay(rightEnd, wallDir);
    const rightEdgeBackward = createRay(rightStart, [-wallDir[0], -wallDir[1]]);

    wallData.push({
      wall,
      index,
      leftStart,
      leftEnd,
      rightStart,
      rightEnd,
      wallDir,
      perpDir,
      thickness: wallThickness,
      actualThickness,
      edges: {
        left: { start: leftStart, end: leftEnd },
        right: { start: rightStart, end: rightEnd },
      },
      rays: {
        leftForward: leftEdgeForward,
        leftBackward: leftEdgeBackward,
        rightForward: rightEdgeForward,
        rightBackward: rightEdgeBackward,
      },
    });

    wallEdgeRays.push(
      { ray: leftEdgeForward, wallIndex: index, edge: 'left', end: 'forward' },
      {
        ray: leftEdgeBackward,
        wallIndex: index,
        edge: 'left',
        end: 'backward',
      },
      {
        ray: rightEdgeForward,
        wallIndex: index,
        edge: 'right',
        end: 'forward',
      },
      {
        ray: rightEdgeBackward,
        wallIndex: index,
        edge: 'right',
        end: 'backward',
      }
    );
  });

  // Find all ray intersections at corners
  const cornerIntersections = new Map();
  const cornerWallPairs = new Map(); // Track which walls meet at each corner
  const tolerance = 0.15; // 15cm tolerance for corner matching (increased from 10cm)

  let cornerCount = 0;
  let skippedParallelCount = 0;
  let potentialCornerCount = 0;

  // First pass: Find all corners and which walls meet there
  wallData.forEach((wall1, i) => {
    wallData.forEach((wall2, j) => {
      if (i >= j) return; // Avoid duplicate checks

      // Check if walls share a corner point
      const wall1Points = [
        { point: wall1.leftStart, type: 'leftStart' },
        { point: wall1.leftEnd, type: 'leftEnd' },
        { point: wall1.rightStart, type: 'rightStart' },
        { point: wall1.rightEnd, type: 'rightEnd' },
      ];

      const wall2Points = [
        { point: wall2.leftStart, type: 'leftStart' },
        { point: wall2.leftEnd, type: 'leftEnd' },
        { point: wall2.rightStart, type: 'rightStart' },
        { point: wall2.rightEnd, type: 'rightEnd' },
      ];

      // Also add wall centerline endpoints for thick wall handling
      // Use dimensions.x for wall length in floor plan (not dimensions.y which is height)
      const wall1Start = wall1.wall.center
        ? [
            wall1.wall.center.x -
              (wall1.wallDir[0] * wall1.wall.dimensions.x) / 2,
            wall1.wall.center.z -
              (wall1.wallDir[1] * wall1.wall.dimensions.x) / 2,
          ]
        : [
            (wall1.leftStart[0] + wall1.rightStart[0]) / 2,
            (wall1.leftStart[1] + wall1.rightStart[1]) / 2,
          ];
      const wall1End = wall1.wall.center
        ? [
            wall1.wall.center.x +
              (wall1.wallDir[0] * wall1.wall.dimensions.x) / 2,
            wall1.wall.center.z +
              (wall1.wallDir[1] * wall1.wall.dimensions.x) / 2,
          ]
        : [
            (wall1.leftEnd[0] + wall1.rightEnd[0]) / 2,
            (wall1.leftEnd[1] + wall1.rightEnd[1]) / 2,
          ];

      const wall2Start = wall2.wall.center
        ? [
            wall2.wall.center.x -
              (wall2.wallDir[0] * wall2.wall.dimensions.x) / 2,
            wall2.wall.center.z -
              (wall2.wallDir[1] * wall2.wall.dimensions.x) / 2,
          ]
        : [
            (wall2.leftStart[0] + wall2.rightStart[0]) / 2,
            (wall2.leftStart[1] + wall2.rightStart[1]) / 2,
          ];
      const wall2End = wall2.wall.center
        ? [
            wall2.wall.center.x +
              (wall2.wallDir[0] * wall2.wall.dimensions.x) / 2,
            wall2.wall.center.z +
              (wall2.wallDir[1] * wall2.wall.dimensions.x) / 2,
          ]
        : [
            (wall2.leftEnd[0] + wall2.rightEnd[0]) / 2,
            (wall2.leftEnd[1] + wall2.rightEnd[1]) / 2,
          ];

      // Track minimum distance for thick wall debugging
      let minDist = Infinity;
      let closestPoints = null;

      // Check all point pairs for proximity
      wall1Points.forEach((p1) => {
        wall2Points.forEach((p2) => {
          const dist = Math.sqrt(
            Math.pow(p1.point[0] - p2.point[0], 2) +
              Math.pow(p1.point[1] - p2.point[1], 2)
          );

          // Track closest points for debugging
          if (dist < minDist) {
            minDist = dist;
            closestPoints = {
              p1,
              p2,
              wall1Thickness: wall1.thickness,
              wall2Thickness: wall2.thickness,
            };
          }

          if (dist < tolerance) {
            potentialCornerCount++;

            // Check if walls are parallel (shouldn't form a corner)
            const wall1Dir =
              wall1.wallDir || wall1.normalizedDir || wall1.direction;
            const wall2Dir =
              wall2.wallDir || wall2.normalizedDir || wall2.direction;

            // If we have direction information, check for parallel walls
            if (wall1Dir && wall2Dir) {
              const dot = Math.abs(
                wall1Dir[0] * wall2Dir[0] + wall1Dir[1] * wall2Dir[1]
              );

              // If walls are nearly parallel (dot product close to 1), skip this corner
              if (dot > 0.98) {
                skippedParallelCount++;
                return;
              }
            }

            // Walls meet at this corner
            const cornerKey = `${Math.round(p1.point[0] / tolerance) * tolerance},${Math.round(p1.point[1] / tolerance) * tolerance}`;

            if (!cornerWallPairs.has(cornerKey)) {
              cornerWallPairs.set(cornerKey, []);
              cornerIntersections.set(cornerKey, []);
              cornerCount++;
            }

            // Add this wall pair and meeting point info
            cornerWallPairs.get(cornerKey).push({
              wall1Index: i,
              wall2Index: j,
              wall1Type: p1.type,
              wall2Type: p2.type,
              cornerPoint: p1.point,
            });
            // Ray processing will be done in second pass
          }
        });
      });

      // Also check centerline endpoints for thick walls
      const centerlinePoints = [
        { w1: wall1Start, w2: wall2Start, type: 'start-start' },
        { w1: wall1Start, w2: wall2End, type: 'start-end' },
        { w1: wall1End, w2: wall2Start, type: 'end-start' },
        { w1: wall1End, w2: wall2End, type: 'end-end' },
      ];

      centerlinePoints.forEach((cp) => {
        const dist = Math.sqrt(
          Math.pow(cp.w1[0] - cp.w2[0], 2) + Math.pow(cp.w1[1] - cp.w2[1], 2)
        );

        // Use a larger tolerance for centerline matching of thick walls
        const thickWallTolerance = tolerance * 2; // 20cm for thick walls

        if (dist < thickWallTolerance) {
          // Check if walls are perpendicular (not parallel)
          const wall1Dir =
            wall1.wallDir || wall1.normalizedDir || wall1.direction;
          const wall2Dir =
            wall2.wallDir || wall2.normalizedDir || wall2.direction;

          if (wall1Dir && wall2Dir) {
            const dot = Math.abs(
              wall1Dir[0] * wall2Dir[0] + wall1Dir[1] * wall2Dir[1]
            );

            if (dot < 0.98) {
              // Not parallel

              // Add this as a corner
              const cornerKey = `${Math.round(cp.w1[0] / tolerance) * tolerance},${Math.round(cp.w1[1] / tolerance) * tolerance}`;

              if (!cornerWallPairs.has(cornerKey)) {
                cornerWallPairs.set(cornerKey, []);
                cornerIntersections.set(cornerKey, []);
                cornerCount++;
              }

              // Determine which endpoints are at the corner
              const wall1Type = cp.type.includes('start')
                ? 'centerStart'
                : 'centerEnd';
              const wall2Type = cp.type.includes('start')
                ? 'centerStart'
                : 'centerEnd';

              cornerWallPairs.get(cornerKey).push({
                wall1Index: i,
                wall2Index: j,
                wall1Type: wall1Type,
                wall2Type: wall2Type,
                cornerPoint: cp.w1,
                isThickWallCorner: true,
              });
            }
          }
        }
      });

      // Log cases where walls are close but not close enough
      if (minDist > tolerance && minDist < tolerance * 3 && closestPoints) {
        // Check if this is likely a thick-thin wall intersection
        const thicknessDiff = Math.abs(
          closestPoints.wall1Thickness - closestPoints.wall2Thickness
        );
      }
    });
  });

  // Second pass: Process rays for each corner
  let intersectionCount = 0;
  let debugCornerCount = 0;

  cornerWallPairs.forEach((wallPairs, cornerKey) => {
    debugCornerCount++;

    // Process each unique wall pair at this corner
    const processedPairs = new Set();

    wallPairs.forEach((pairInfo) => {
      const pairKey = `${Math.min(pairInfo.wall1Index, pairInfo.wall2Index)}-${Math.max(pairInfo.wall1Index, pairInfo.wall2Index)}`;
      if (processedPairs.has(pairKey)) return;
      processedPairs.add(pairKey);

      const wall1 = wallData[pairInfo.wall1Index];
      const wall2 = wallData[pairInfo.wall2Index];

      // Get all rays from both walls
      const wall1Rays = [];
      const wall2Rays = [];

      // For wall1, get rays based on which end is at the corner
      if (pairInfo.wall1Type.toLowerCase().includes('start')) {
        wall1Rays.push({ ray: wall1.rays.leftBackward, type: 'leftBackward' });
        wall1Rays.push({
          ray: wall1.rays.rightBackward,
          type: 'rightBackward',
        });
      } else {
        wall1Rays.push({ ray: wall1.rays.leftForward, type: 'leftForward' });
        wall1Rays.push({ ray: wall1.rays.rightForward, type: 'rightForward' });
      }

      // For wall2, get rays based on which end is at the corner
      if (pairInfo.wall2Type.toLowerCase().includes('start')) {
        wall2Rays.push({ ray: wall2.rays.leftBackward, type: 'leftBackward' });
        wall2Rays.push({
          ray: wall2.rays.rightBackward,
          type: 'rightBackward',
        });
      } else {
        wall2Rays.push({ ray: wall2.rays.leftForward, type: 'leftForward' });
        wall2Rays.push({ ray: wall2.rays.rightForward, type: 'rightForward' });
      }

      // Try all ray combinations
      wall1Rays.forEach((r1) => {
        wall2Rays.forEach((r2) => {
          // Check if rays are overlapping (same origin and very similar direction)
          const originDist = Math.sqrt(
            Math.pow(r1.ray.origin[0] - r2.ray.origin[0], 2) +
              Math.pow(r1.ray.origin[1] - r2.ray.origin[1], 2)
          );

          const dot = Math.abs(
            r1.ray.direction[0] * r2.ray.direction[0] +
              r1.ray.direction[1] * r2.ray.direction[1]
          );

          // Skip if rays have very close origins and are parallel (likely duplicates)
          if (originDist < 0.01 && dot > 0.99) {
            return;
          }

          if (dot > 0.95) {
            return;
          }

          const intersection = rayIntersection(r1.ray, r2.ray);
          if (intersection) {
            const distToCorner = Math.sqrt(
              Math.pow(intersection[0] - pairInfo.cornerPoint[0], 2) +
                Math.pow(intersection[1] - pairInfo.cornerPoint[1], 2)
            );

            // For thick wall corners, use a more generous distance threshold
            const maxDist = pairInfo.isThickWallCorner
              ? Math.max(wall1.thickness, wall2.thickness) * 2.0 // More generous for thick walls
              : Math.max(wall1.thickness, wall2.thickness) * 1.5; // Tighter constraint for normal walls

            if (distToCorner < maxDist) {
              intersectionCount++;

              cornerIntersections.get(cornerKey).push({
                point: intersection,
                wall1Index: pairInfo.wall1Index,
                wall2Index: pairInfo.wall2Index,
                wall1Type: r1.type,
                wall2Type: r2.type,
                corner: pairInfo.cornerPoint,
              });
            }
          }
        });
      });
    });
  });

  // Process corner intersections to fill gaps
  const cornerFills = [];
  const processedCornerLocations = new Set(); // Track unique corner locations

  cornerIntersections.forEach((intersections, cornerKey) => {
    if (intersections.length < 2) return;

    const [cornerX, cornerY] = cornerKey.split(',').map(parseFloat);

    // Check if we've already processed a corner at this location
    let skip = false;
    for (const processed of processedCornerLocations) {
      const [px, py] = processed.split(',').map(parseFloat);
      const dist = Math.sqrt(
        Math.pow(px - cornerX, 2) + Math.pow(py - cornerY, 2)
      );
      if (dist < 0.05) {
        // Within 5cm, consider it the same corner
        skip = true;
        break;
      }
    }

    if (skip) return;

    // Collect all unique intersection points for this corner
    const uniquePoints = new Map();
    const wallIndices = new Set();

    // Get the wall indices that actually meet at this corner
    const wallPairsAtCorner = new Set();
    intersections.forEach((int) => {
      const pairKey = `${Math.min(int.wall1Index, int.wall2Index)}-${Math.max(int.wall1Index, int.wall2Index)}`;
      wallPairsAtCorner.add(pairKey);
    });

    // Only use intersections from the primary wall pair at this corner
    if (wallPairsAtCorner.size > 0) {
      // Debug: log all wall pairs at this corner
      if (cornerX.toFixed(1) === '-0.6' && cornerY.toFixed(1) === '-1.9') {
        console.log(
          `Corner [-0.6, -1.9] has ${wallPairsAtCorner.size} wall pairs:`,
          Array.from(wallPairsAtCorner)
        );
      }

      const primaryPair = Array.from(wallPairsAtCorner)[0]; // Use first pair
      const [wall1Idx, wall2Idx] = primaryPair.split('-').map(Number);

      if (cornerX.toFixed(1) === '-0.6' && cornerY.toFixed(1) === '-1.9') {
        console.log(`Using primary pair: walls ${wall1Idx} and ${wall2Idx}`);
      }

      intersections.forEach((int) => {
        // Only include intersections from the primary wall pair
        if (
          (int.wall1Index === wall1Idx && int.wall2Index === wall2Idx) ||
          (int.wall1Index === wall2Idx && int.wall2Index === wall1Idx)
        ) {
          const pointKey = `${int.point[0].toFixed(3)},${int.point[1].toFixed(3)}`;

          if (cornerX.toFixed(1) === '-0.6' && cornerY.toFixed(1) === '-1.9') {
            console.log(
              `  Adding point: [${int.point[0].toFixed(3)}, ${int.point[1].toFixed(3)}] from walls ${int.wall1Index}-${int.wall2Index}`
            );
          }

          uniquePoints.set(pointKey, {
            point: int.point,
            wall1Type: int.wall1Type,
            wall2Type: int.wall2Type,
          });
          wallIndices.add(int.wall1Index);
          wallIndices.add(int.wall2Index);
        }
      });
    }

    // Need exactly 4 points for a proper corner fill quadrilateral
    if (uniquePoints.size === 4) {
      const fillPoints = Array.from(uniquePoints.values()).map((p) => p.point);

      // Sort points to create a non-self-intersecting polygon
      // First, find the centroid of all points
      const centroid = fillPoints.reduce(
        (acc, pt) => {
          return [
            acc[0] + pt[0] / fillPoints.length,
            acc[1] + pt[1] / fillPoints.length,
          ];
        },
        [0, 0]
      );

      // Sort by angle from centroid (not corner)
      fillPoints.sort((a, b) => {
        const angleA = Math.atan2(a[1] - centroid[1], a[0] - centroid[0]);
        const angleB = Math.atan2(b[1] - centroid[1], b[0] - centroid[0]);
        return angleA - angleB;
      });

      // Check for bow-tie by seeing if polygon edges intersect
      const lineIntersects = (p1, p2, p3, p4) => {
        const det =
          (p2[0] - p1[0]) * (p4[1] - p3[1]) - (p2[1] - p1[1]) * (p4[0] - p3[0]);
        if (Math.abs(det) < 0.0001) return false;

        const t =
          ((p3[0] - p1[0]) * (p4[1] - p3[1]) -
            (p3[1] - p1[1]) * (p4[0] - p3[0])) /
          det;
        const u =
          ((p3[0] - p1[0]) * (p2[1] - p1[1]) -
            (p3[1] - p1[1]) * (p2[0] - p1[0])) /
          det;

        return t > 0.01 && t < 0.99 && u > 0.01 && u < 0.99;
      };

      // Create the fill polygon
      cornerFills.push({
        corner: [cornerX, cornerY],
        points: fillPoints,
        wallIndices: Array.from(wallIndices),
      });

      // Mark this corner location as processed
      processedCornerLocations.add(cornerKey);
    } else if (uniquePoints.size === 2) {
      // Skip corners with only 2 intersection points to avoid bow-tie shapes
    } else if (uniquePoints.size === 3) {
      // For 3 points, check if they form a reasonable triangle
      const pointData = Array.from(uniquePoints.values());
      const fillPoints = pointData.map((p) => p.point);

      // Find the point farthest from the corner
      let maxDistFromCorner = 0;
      let farthestPointIndex = -1;
      pointData.forEach((data, i) => {
        const pt = data.point;
        const dist = Math.sqrt(
          Math.pow(pt[0] - cornerX, 2) + Math.pow(pt[1] - cornerY, 2)
        );

        if (dist > maxDistFromCorner) {
          maxDistFromCorner = dist;
          farthestPointIndex = i;
        }
      });

      // If one point is much farther than the others, skip it
      const avgDistOthers =
        fillPoints.reduce((sum, pt, i) => {
          if (i === farthestPointIndex) return sum;
          const dist = Math.sqrt(
            Math.pow(pt[0] - cornerX, 2) + Math.pow(pt[1] - cornerY, 2)
          );
          return sum + dist;
        }, 0) / 2;

      if (maxDistFromCorner > avgDistOthers * 2.5) {
        // Remove the outlier point
        fillPoints.splice(farthestPointIndex, 1);

        // Now we only have 2 points, skip this corner
        return;
      }

      // Check if any two points are very close (might be duplicates)
      for (let i = 0; i < fillPoints.length - 1; i++) {
        for (let j = i + 1; j < fillPoints.length; j++) {
          const dist = Math.sqrt(
            Math.pow(fillPoints[i][0] - fillPoints[j][0], 2) +
              Math.pow(fillPoints[i][1] - fillPoints[j][1], 2)
          );
          if (dist < 0.01) {
            console.log(
              `  WARNING: Points ${i} and ${j} are very close (${dist.toFixed(4)}m apart)`
            );
          }
        }
      }

      // Sort points to create a non-self-intersecting polygon
      // First, find the centroid of all points
      const centroid = fillPoints.reduce(
        (acc, pt) => {
          return [
            acc[0] + pt[0] / fillPoints.length,
            acc[1] + pt[1] / fillPoints.length,
          ];
        },
        [0, 0]
      );

      // Sort by angle from centroid (not corner)
      fillPoints.sort((a, b) => {
        const angleA = Math.atan2(a[1] - centroid[1], a[0] - centroid[0]);
        const angleB = Math.atan2(b[1] - centroid[1], b[0] - centroid[0]);
        return angleA - angleB;
      });

      // Debug: Log the sorted points for specific corners
      if (Math.abs(cornerX - -0.6) < 0.1 && Math.abs(cornerY - -1.9) < 0.1) {
        console.log(
          `Sorted points for corner [${cornerX.toFixed(3)}, ${cornerY.toFixed(3)}]:`
        );
        fillPoints.forEach((pt, i) => {
          const angle =
            (Math.atan2(pt[1] - cornerY, pt[0] - cornerX) * 180) / Math.PI;
          console.log(
            `  ${i}: [${pt[0].toFixed(3)}, ${pt[1].toFixed(3)}] angle: ${angle.toFixed(1)}°`
          );
        });
      }

      cornerFills.push({
        corner: [cornerX, cornerY],
        points: fillPoints,
        wallIndices: Array.from(wallIndices),
      });

      processedCornerLocations.add(cornerKey);
    }
  });

  // Second pass: draw walls
  wallData.forEach((data, index) => {
    const {
      wall,
      leftStart,
      leftEnd,
      rightStart,
      rightEnd,
      perpDir,
      thickness,
      actualThickness,
    } = data;

    if (
      !wall.center ||
      wall.center.x === undefined ||
      wall.center.z === undefined ||
      !wall.dimensions
    ) {
      return;
    }

    // Build corners with potential adjustments from ray intersections
    let corners = [leftStart, leftEnd, rightEnd, rightStart];

    // Check for NaN in corners for edited walls
    if (wall.isWallEdited) {
      const hasNaN = corners.some(
        (corner) =>
          !corner || corner.length < 2 || isNaN(corner[0]) || isNaN(corner[1])
      );
      if (hasNaN) {
        console.error(`Edited wall ${index} has NaN in corners:`, {
          corners,
          leftStart,
          leftEnd,
          rightStart,
          rightEnd,
          wall: {
            center: wall.center,
            dimensions: wall.dimensions,
            rotation: wall.rotation,
          },
        });
        return; // Skip drawing this wall
      }
    }

    // Apply corner adjustments if available
    if (!debugMode) {
      // Check each corner for intersections
      const cornerPoints = [
        { original: leftStart, index: 0, type: 'leftStart' },
        { original: leftEnd, index: 1, type: 'leftEnd' },
        { original: rightEnd, index: 2, type: 'rightEnd' },
        { original: rightStart, index: 3, type: 'rightStart' },
      ];

      cornerPoints.forEach((cp) => {
        const cornerKey = `${Math.round(cp.original[0] / tolerance) * tolerance},${Math.round(cp.original[1] / tolerance) * tolerance}`;
        const intersections = cornerIntersections.get(cornerKey);

        if (intersections && intersections.length > 0) {
          // Find the intersection involving this wall
          const relevantIntersection = intersections.find(
            (int) =>
              (int.wall1Index === index && int.wall1Type === cp.type) ||
              (int.wall2Index === index && int.wall2Type === cp.type)
          );

          if (relevantIntersection) {
            corners[cp.index] = relevantIntersection.point;
          }
        }
      });
    }

    // Check if this wall is selected
    // For legacy scanned walls where room wall and structure wall share the
    // same ID, only highlight the structure wall (drawn in the pre-pass) to
    // avoid double-highlighting both the thick grey wall and the thin room wall.
    const idMatch =
      selectedItem && selectedItem.id && wall.id && selectedItem.id === wall.id;
    const isSelectedStructureOnly =
      idMatch && structureWalls?.some((sw) => sw.id === selectedItem.id);
    const isSelected = idMatch && !isSelectedStructureOnly;

    // Get windows, doors, and openings on this wall
    const windowsOnWall = getWindowsOnWall(wall, windows);
    const doorsOnWall = getDoorsOnWall(wall, doors);
    const openingsOnWall = getOpeningsOnWall(wall, openings);

    // Set up drawing style
    const hasThickness = wall.dimensions.z && wall.dimensions.z > 0.001; // More than 1mm

    // Check alignment status for color coding
    const alignmentStatus = wall.alignment?.status;

    // Check if wall was extended/shrunk
    const wallExtension = wallExtensionMetadata[wall.id];
    const isExtended = wallExtension?.type === 'extended';
    const isShrunk = wallExtension?.type === 'shrunk';

    if (hasThickness) {
      // Fill with color based on debug mode and structure level
      if (debugMode) {
        ctx.fillStyle = isSelected
          ? '#ff6666'
          : isExtended
            ? '#FF0000' // BRIGHT RED for extended walls
            : isShrunk
              ? '#FF69B4' // Hot pink for shrunk walls
              : alignmentStatus === 'unmatched'
                ? '#FF8C00' // Dark orange for unmatched walls
                : alignmentStatus === 'unrefined'
                  ? '#FFA500' // Orange for unrefined walls
                  : wall._isStructureLevel
                    ? '#9B59B6' // Purple for structure-level walls in debug mode
                    : getRandomWallColor(wall.id);
      } else {
        // Non-debug mode: dark grey or purple for structure
        ctx.fillStyle = isSelected
          ? HIGHLIGHT_COLORS.WALL_SELECTED_FILL
          : wall._isStructureLevel
            ? '#8B00FF' // Purple for structure-level walls
            : ELEMENT_COLORS.WALL_NORMAL;
      }
    } else {
      // Walls with zero thickness
      ctx.fillStyle = isSelected
        ? HIGHLIGHT_COLORS.WALL_SELECTED_FILL
        : debugMode && isExtended
          ? '#FF0000' // BRIGHT RED for extended walls with zero thickness
          : debugMode && isShrunk
            ? '#FF69B4' // Hot pink for shrunk walls with zero thickness
            : debugMode && alignmentStatus === 'unmatched'
              ? '#FF8C00' // Dark orange for unmatched walls only in debug mode
              : debugMode && alignmentStatus === 'unrefined'
                ? '#FFA500' // Orange for unrefined walls only in debug mode
                : wall._isStructureLevel
                  ? '#8B00FF' // Purple for structure-level walls with zero thickness
                  : showRoomWallsBold
                    ? '#000000' // Black for room walls when bold mode is on
                    : debugMode
                      ? ELEMENT_COLORS.WALL_BLACK
                      : ELEMENT_COLORS.WALL_NORMAL;
    }

    // A guard rail is a low wall standing over a void, not drywall, so it keeps
    // its own colour. Selection colour still wins.
    const isRailing = isRailingWall(wall);
    if (isRailing && !isSelected && !debugMode) {
      ctx.fillStyle = RAILING_COLOR;
    }

    if (
      windowsOnWall.length === 0 &&
      doorsOnWall.length === 0 &&
      openingsOnWall.length === 0
    ) {
      // No windows, doors, or openings - draw wall normally
      ctx.beginPath();
      ctx.moveTo(corners[0][0], corners[0][1]);

      for (let i = 1; i < corners.length; i++) {
        ctx.lineTo(corners[i][0], corners[i][1]);
      }

      ctx.closePath();
      ctx.fill();

      // Draw outline for walls with thickness
      if (hasThickness) {
        ctx.strokeStyle = ELEMENT_COLORS.WALL_BLACK;
        ctx.lineWidth = Math.max(0.02, 2 / scale);
        ctx.stroke();
      }
    } else {
      // Wall has windows and/or doors - need to create gaps
      const wallDir = data.wallDir;
      const halfLength = wall.dimensions.x / 2;

      // Calculate positions of all openings (windows and doors) along the wall
      const openingSegments = [];

      // Add window segments
      windowsOnWall.forEach((window) => {
        // Project window center onto wall
        const wallToWindow = [
          window.center.x - wall.center.x,
          window.center.z - wall.center.z,
        ];
        const projectionOnWall =
          wallToWindow[0] * wallDir[0] + wallToWindow[1] * wallDir[1];

        const halfWindowWidth = window.dimensions.x / 2;
        openingSegments.push({
          start: projectionOnWall - halfWindowWidth,
          end: projectionOnWall + halfWindowWidth,
          type: 'window',
          item: window,
        });
      });

      // Add door segments
      doorsOnWall.forEach((door) => {
        // Project door center onto wall
        const wallToDoor = [
          door.center.x - wall.center.x,
          door.center.z - wall.center.z,
        ];
        const projectionOnWall =
          wallToDoor[0] * wallDir[0] + wallToDoor[1] * wallDir[1];

        const halfDoorWidth = (door.dimensions.x || door.dimensions.y) / 2;
        openingSegments.push({
          start: projectionOnWall - halfDoorWidth,
          end: projectionOnWall + halfDoorWidth,
          type: 'door',
          item: door,
        });
      });

      // Add opening segments
      openingsOnWall.forEach((opening) => {
        // Project opening center onto wall
        const wallToOpening = [
          opening.center.x - wall.center.x,
          opening.center.z - wall.center.z,
        ];
        const projectionOnWall =
          wallToOpening[0] * wallDir[0] + wallToOpening[1] * wallDir[1];

        const halfOpeningWidth = (opening.dimensions?.x || 0.8) / 2;
        openingSegments.push({
          start: projectionOnWall - halfOpeningWidth,
          end: projectionOnWall + halfOpeningWidth,
          type: 'opening',
          item: opening,
        });
      });

      // Sort segments by start position
      openingSegments.sort((a, b) => a.start - b.start);

      // Draw wall segments with gaps for openings
      ctx.save();

      // Create clipping path for the wall outline
      ctx.beginPath();
      ctx.moveTo(corners[0][0], corners[0][1]);
      for (let i = 1; i < corners.length; i++) {
        ctx.lineTo(corners[i][0], corners[i][1]);
      }
      ctx.closePath();
      ctx.clip();

      // Fill the entire wall first
      ctx.fill();

      // Then cut out openings (windows and doors)
      ctx.globalCompositeOperation = 'destination-out';

      openingSegments.forEach((segment) => {
        // Calculate window gap corners
        const startRatio = (segment.start + halfLength) / wall.dimensions.x;
        const endRatio = (segment.end + halfLength) / wall.dimensions.x;

        const gapLeftStart = [
          leftStart[0] + (leftEnd[0] - leftStart[0]) * startRatio,
          leftStart[1] + (leftEnd[1] - leftStart[1]) * startRatio,
        ];
        const gapLeftEnd = [
          leftStart[0] + (leftEnd[0] - leftStart[0]) * endRatio,
          leftStart[1] + (leftEnd[1] - leftStart[1]) * endRatio,
        ];
        const gapRightEnd = [
          rightStart[0] + (rightEnd[0] - rightStart[0]) * endRatio,
          rightStart[1] + (rightEnd[1] - rightStart[1]) * endRatio,
        ];
        const gapRightStart = [
          rightStart[0] + (rightEnd[0] - rightStart[0]) * startRatio,
          rightStart[1] + (rightEnd[1] - rightStart[1]) * startRatio,
        ];

        ctx.beginPath();
        ctx.moveTo(gapLeftStart[0], gapLeftStart[1]);
        ctx.lineTo(gapLeftEnd[0], gapLeftEnd[1]);
        ctx.lineTo(gapRightEnd[0], gapRightEnd[1]);
        ctx.lineTo(gapRightStart[0], gapRightStart[1]);
        ctx.closePath();
        ctx.fill();
      });

      // Reset composite operation
      ctx.globalCompositeOperation = 'source-over';

      // Draw light grey fill for opening gaps (non-bold mode only for "opening" type)
      openingSegments.forEach((segment) => {
        if (segment.type === 'opening') {
          ctx.save();
          ctx.fillStyle = '#E0E0E0'; // Light grey for openings

          // Calculate gap corners
          const startRatio = (segment.start + halfLength) / wall.dimensions.x;
          const endRatio = (segment.end + halfLength) / wall.dimensions.x;

          const gapLeftStart = [
            leftStart[0] + (leftEnd[0] - leftStart[0]) * startRatio,
            leftStart[1] + (leftEnd[1] - leftStart[1]) * startRatio,
          ];
          const gapLeftEnd = [
            leftStart[0] + (leftEnd[0] - leftStart[0]) * endRatio,
            leftStart[1] + (leftEnd[1] - leftStart[1]) * endRatio,
          ];
          const gapRightEnd = [
            rightStart[0] + (rightEnd[0] - rightStart[0]) * endRatio,
            rightStart[1] + (rightEnd[1] - rightStart[1]) * endRatio,
          ];
          const gapRightStart = [
            rightStart[0] + (rightEnd[0] - rightStart[0]) * startRatio,
            rightStart[1] + (rightEnd[1] - rightStart[1]) * startRatio,
          ];

          // Fill the opening gap with light grey
          ctx.beginPath();
          ctx.moveTo(gapLeftStart[0], gapLeftStart[1]);
          ctx.lineTo(gapLeftEnd[0], gapLeftEnd[1]);
          ctx.lineTo(gapRightEnd[0], gapRightEnd[1]);
          ctx.lineTo(gapRightStart[0], gapRightStart[1]);
          ctx.closePath();
          ctx.fill();

          ctx.restore();
        }
      });

      // Draw outline for walls with thickness
      if (hasThickness) {
        ctx.strokeStyle = ELEMENT_COLORS.WALL_BLACK;
        ctx.lineWidth = Math.max(0.02, 2 / scale);

        // Draw wall outline with gaps
        let currentPos = -halfLength;

        openingSegments.forEach((segment, idx) => {
          // Draw wall segment before opening
          if (segment.start > -halfLength) {
            const startRatio = Math.max(
              0,
              (currentPos + halfLength) / wall.dimensions.x
            );
            const endRatio = (segment.start + halfLength) / wall.dimensions.x;

            if (endRatio > startRatio) {
              ctx.beginPath();

              // Left edge
              const leftStartPt = [
                leftStart[0] + (leftEnd[0] - leftStart[0]) * startRatio,
                leftStart[1] + (leftEnd[1] - leftStart[1]) * startRatio,
              ];
              const leftEndPt = [
                leftStart[0] + (leftEnd[0] - leftStart[0]) * endRatio,
                leftStart[1] + (leftEnd[1] - leftStart[1]) * endRatio,
              ];

              // Right edge
              const rightStartPt = [
                rightStart[0] + (rightEnd[0] - rightStart[0]) * startRatio,
                rightStart[1] + (rightEnd[1] - rightStart[1]) * startRatio,
              ];
              const rightEndPt = [
                rightStart[0] + (rightEnd[0] - rightStart[0]) * endRatio,
                rightStart[1] + (rightEnd[1] - rightStart[1]) * endRatio,
              ];

              // Draw segment outline
              ctx.moveTo(leftStartPt[0], leftStartPt[1]);
              ctx.lineTo(leftEndPt[0], leftEndPt[1]);
              ctx.moveTo(rightStartPt[0], rightStartPt[1]);
              ctx.lineTo(rightEndPt[0], rightEndPt[1]);

              // Connect edges if at wall start
              if (currentPos === -halfLength) {
                ctx.moveTo(leftStartPt[0], leftStartPt[1]);
                ctx.lineTo(rightStartPt[0], rightStartPt[1]);
              }

              ctx.stroke();
            }
          }

          currentPos = segment.end;
        });

        // Draw final segment after last opening
        if (currentPos < halfLength) {
          const startRatio = (currentPos + halfLength) / wall.dimensions.x;
          const endRatio = 1;

          ctx.beginPath();

          // Left edge
          const leftStartPt = [
            leftStart[0] + (leftEnd[0] - leftStart[0]) * startRatio,
            leftStart[1] + (leftEnd[1] - leftStart[1]) * startRatio,
          ];

          // Right edge
          const rightStartPt = [
            rightStart[0] + (rightEnd[0] - rightStart[0]) * startRatio,
            rightStart[1] + (rightEnd[1] - rightStart[1]) * startRatio,
          ];

          // Draw segment outline
          ctx.moveTo(leftStartPt[0], leftStartPt[1]);
          ctx.lineTo(leftEnd[0], leftEnd[1]);
          ctx.moveTo(rightStartPt[0], rightStartPt[1]);
          ctx.lineTo(rightEnd[0], rightEnd[1]);

          // Connect edges at wall end
          ctx.moveTo(leftEnd[0], leftEnd[1]);
          ctx.lineTo(rightEnd[0], rightEnd[1]);

          ctx.stroke();
        }
      }

      ctx.restore();

      // Draw highlighted edges for selected doors/windows/openings.
      // Fill the gap with a semi-transparent pink + stroke a closed
      // rectangle around it so the WHOLE opening reads as highlighted
      // (not just the two end-cap lines). Previously this drew 4
      // separate edges; on a thin wall (~6") the long sides ran inside
      // the wall fill and the only visible mark was the two end caps.
      openingSegments.forEach((segment) => {
        const isOpeningSelected = matchesOpeningSelection(
          segment.item,
          selectedItem
        );

        if (isOpeningSelected) {
          ctx.save();

          // Calculate gap edge positions
          const startRatio = (segment.start + halfLength) / wall.dimensions.x;
          const endRatio = (segment.end + halfLength) / wall.dimensions.x;

          const gapLeftStart = [
            leftStart[0] + (leftEnd[0] - leftStart[0]) * startRatio,
            leftStart[1] + (leftEnd[1] - leftStart[1]) * startRatio,
          ];
          const gapLeftEnd = [
            leftStart[0] + (leftEnd[0] - leftStart[0]) * endRatio,
            leftStart[1] + (leftEnd[1] - leftStart[1]) * endRatio,
          ];
          const gapRightEnd = [
            rightStart[0] + (rightEnd[0] - rightStart[0]) * endRatio,
            rightStart[1] + (rightEnd[1] - rightStart[1]) * endRatio,
          ];
          const gapRightStart = [
            rightStart[0] + (rightEnd[0] - rightStart[0]) * startRatio,
            rightStart[1] + (rightEnd[1] - rightStart[1]) * startRatio,
          ];

          // Fill the gap interior with the same pink at 40% so the whole
          // opening reads as highlighted.
          ctx.fillStyle = HIGHLIGHT_COLORS.PRIMARY_FILL;
          ctx.beginPath();
          ctx.moveTo(gapLeftStart[0], gapLeftStart[1]);
          ctx.lineTo(gapLeftEnd[0], gapLeftEnd[1]);
          ctx.lineTo(gapRightEnd[0], gapRightEnd[1]);
          ctx.lineTo(gapRightStart[0], gapRightStart[1]);
          ctx.closePath();
          ctx.fill();

          // Outline the same rectangle with the thicker pink stroke.
          ctx.strokeStyle = HIGHLIGHT_COLORS.PRIMARY;
          ctx.lineWidth = HIGHLIGHT_COLORS.THICK_LINE(scale);
          ctx.stroke();

          ctx.restore();
        }
      });

      // Draw selection outline if selected (for walls with openings)
      if (isSelected) {
        // First, draw pink outlines around all openings to show wall continues through them
        ctx.save();
        ctx.strokeStyle = HIGHLIGHT_COLORS.PRIMARY;
        ctx.lineWidth = HIGHLIGHT_COLORS.THICK_LINE(scale);

        openingSegments.forEach((segment) => {
          // Calculate gap edge positions
          const startRatio = (segment.start + halfLength) / wall.dimensions.x;
          const endRatio = (segment.end + halfLength) / wall.dimensions.x;

          const gapLeftStart = [
            leftStart[0] + (leftEnd[0] - leftStart[0]) * startRatio,
            leftStart[1] + (leftEnd[1] - leftStart[1]) * startRatio,
          ];
          const gapLeftEnd = [
            leftStart[0] + (leftEnd[0] - leftStart[0]) * endRatio,
            leftStart[1] + (leftEnd[1] - leftStart[1]) * endRatio,
          ];
          const gapRightEnd = [
            rightStart[0] + (rightEnd[0] - rightStart[0]) * endRatio,
            rightStart[1] + (rightEnd[1] - rightStart[1]) * endRatio,
          ];
          const gapRightStart = [
            rightStart[0] + (rightEnd[0] - rightStart[0]) * startRatio,
            rightStart[1] + (rightEnd[1] - rightStart[1]) * startRatio,
          ];

          // Draw the four edges of the gap
          ctx.beginPath();
          ctx.moveTo(gapLeftStart[0], gapLeftStart[1]);
          ctx.lineTo(gapRightStart[0], gapRightStart[1]);
          ctx.moveTo(gapLeftEnd[0], gapLeftEnd[1]);
          ctx.lineTo(gapRightEnd[0], gapRightEnd[1]);
          ctx.moveTo(gapLeftStart[0], gapLeftStart[1]);
          ctx.lineTo(gapLeftEnd[0], gapLeftEnd[1]);
          ctx.moveTo(gapRightStart[0], gapRightStart[1]);
          ctx.lineTo(gapRightEnd[0], gapRightEnd[1]);
          ctx.stroke();
        });

        ctx.restore();

        // Then draw the wall segment outlines
        ctx.save();
        ctx.strokeStyle = HIGHLIGHT_COLORS.WALL_SELECTED;
        ctx.lineWidth = Math.max(0.02, 3 / scale);

        // Draw outline around all wall segments (not the gaps)
        let currentPos = -halfLength;

        openingSegments.forEach((segment, idx) => {
          // Draw wall segment outline before opening
          if (segment.start > currentPos) {
            const startRatio = Math.max(
              0,
              (currentPos + halfLength) / wall.dimensions.x
            );
            const endRatio = (segment.start + halfLength) / wall.dimensions.x;

            if (endRatio > startRatio) {
              ctx.beginPath();

              // Left edge
              const leftStartPt = [
                leftStart[0] + (leftEnd[0] - leftStart[0]) * startRatio,
                leftStart[1] + (leftEnd[1] - leftStart[1]) * startRatio,
              ];
              const leftEndPt = [
                leftStart[0] + (leftEnd[0] - leftStart[0]) * endRatio,
                leftStart[1] + (leftEnd[1] - leftStart[1]) * endRatio,
              ];

              // Right edge
              const rightStartPt = [
                rightStart[0] + (rightEnd[0] - rightStart[0]) * startRatio,
                rightStart[1] + (rightEnd[1] - rightStart[1]) * startRatio,
              ];
              const rightEndPt = [
                rightStart[0] + (rightEnd[0] - rightStart[0]) * endRatio,
                rightStart[1] + (rightEnd[1] - rightStart[1]) * endRatio,
              ];

              // Draw segment outline
              ctx.moveTo(leftStartPt[0], leftStartPt[1]);
              ctx.lineTo(leftEndPt[0], leftEndPt[1]);
              ctx.lineTo(rightEndPt[0], rightEndPt[1]);
              ctx.lineTo(rightStartPt[0], rightStartPt[1]);
              ctx.closePath();
              ctx.stroke();
            }
          }

          currentPos = segment.end;
        });

        // Draw final segment outline after last opening
        if (currentPos < halfLength) {
          const startRatio = (currentPos + halfLength) / wall.dimensions.x;
          const endRatio = 1;

          ctx.beginPath();

          // Left edge
          const leftStartPt = [
            leftStart[0] + (leftEnd[0] - leftStart[0]) * startRatio,
            leftStart[1] + (leftEnd[1] - leftStart[1]) * startRatio,
          ];

          // Right edge
          const rightStartPt = [
            rightStart[0] + (rightEnd[0] - rightStart[0]) * startRatio,
            rightStart[1] + (rightEnd[1] - rightStart[1]) * startRatio,
          ];

          // Draw segment outline
          ctx.moveTo(leftStartPt[0], leftStartPt[1]);
          ctx.lineTo(leftEnd[0], leftEnd[1]);
          ctx.lineTo(rightEnd[0], rightEnd[1]);
          ctx.lineTo(rightStartPt[0], rightStartPt[1]);
          ctx.closePath();
          ctx.stroke();
        }

        // Draw segment outline if no openings at the start
        if (
          openingSegments.length === 0 ||
          openingSegments[0].start > -halfLength
        ) {
          const endRatio =
            openingSegments.length > 0
              ? (openingSegments[0].start + halfLength) / wall.dimensions.x
              : 1;

          ctx.beginPath();
          ctx.moveTo(leftStart[0], leftStart[1]);
          if (openingSegments.length > 0) {
            const leftEndPt = [
              leftStart[0] + (leftEnd[0] - leftStart[0]) * endRatio,
              leftStart[1] + (leftEnd[1] - leftStart[1]) * endRatio,
            ];
            const rightEndPt = [
              rightStart[0] + (rightEnd[0] - rightStart[0]) * endRatio,
              rightStart[1] + (rightEnd[1] - rightStart[1]) * endRatio,
            ];
            ctx.lineTo(leftEndPt[0], leftEndPt[1]);
            ctx.lineTo(rightEndPt[0], rightEndPt[1]);
          } else {
            ctx.lineTo(leftEnd[0], leftEnd[1]);
            ctx.lineTo(rightEnd[0], rightEnd[1]);
          }
          ctx.lineTo(rightStart[0], rightStart[1]);
          ctx.closePath();
          ctx.stroke();
        }

        ctx.restore();
      }
    }

    // Draw selection outline if selected
    if (isSelected) {
      ctx.strokeStyle = HIGHLIGHT_COLORS.WALL_SELECTED;
      ctx.lineWidth = Math.max(0.02, 3 / scale);
      ctx.stroke();
    }

    // Store wall normal data for later drawing
    wallNormals.push({
      centerX: wall.center.x,
      centerZ: wall.center.z,
      perpDir: perpDir,
      thickness: thickness,
      alignmentStatus: wall.alignment?.status, // Include alignment status for color coding
    });
  });

  // Third pass: draw corner fills
  ctx.save();

  cornerFills.forEach((fill, idx) => {
    if (fill.points.length < 3) return; // Need at least 3 points for a polygon

    // Check if this is a reasonable shape to fill
    let shouldDraw = true;

    if (fill.points.length === 3) {
      // For triangles, check if it's too elongated
      const [p1, p2, p3] = fill.points;
      const d12 = Math.sqrt(
        Math.pow(p1[0] - p2[0], 2) + Math.pow(p1[1] - p2[1], 2)
      );
      const d23 = Math.sqrt(
        Math.pow(p2[0] - p3[0], 2) + Math.pow(p2[1] - p3[1], 2)
      );
      const d31 = Math.sqrt(
        Math.pow(p3[0] - p1[0], 2) + Math.pow(p3[1] - p1[1], 2)
      );

      const maxSide = Math.max(d12, d23, d31);
      const minSide = Math.min(d12, d23, d31);

      // Skip very elongated triangles
      if (maxSide > minSide * 2.5) {
        shouldDraw = false;
      }

      // Also skip if the triangle is too small
      if (maxSide < 0.05) {
        // Less than 5cm
        shouldDraw = false;
      }
    }

    if (shouldDraw) {
      // Add some size-based debugging
      let fillArea = 0;
      if (fill.points.length >= 3) {
        // Calculate area using shoelace formula
        for (let i = 0; i < fill.points.length; i++) {
          const j = (i + 1) % fill.points.length;
          fillArea += fill.points[i][0] * fill.points[j][1];
          fillArea -= fill.points[j][0] * fill.points[i][1];
        }
        fillArea = Math.abs(fillArea) / 2;
      }

      // Log corner fills that might be phantom walls
      // Check for suspiciously positioned fills
      const centerX =
        fill.points.reduce((sum, p) => sum + p[0], 0) / fill.points.length;
      const centerY =
        fill.points.reduce((sum, p) => sum + p[1], 0) / fill.points.length;
      const distFromCorner = Math.sqrt(
        Math.pow(centerX - fill.corner[0], 2) +
          Math.pow(centerY - fill.corner[1], 2)
      );

      // Only fill corners in non-debug mode
      if (!debugMode) {
        // Draw fill with wall color in non-debug mode
        ctx.fillStyle = '#404040';
        ctx.strokeStyle = 'rgba(255, 0, 0, 0.2)'; // Light red outline for debugging
        ctx.lineWidth = 1 / scale;

        // Draw the fill polygon
        ctx.beginPath();
        // Start with the first point (not the corner)
        ctx.moveTo(fill.points[0][0], fill.points[0][1]);
        // Draw lines to subsequent points
        for (let i = 1; i < fill.points.length; i++) {
          ctx.lineTo(fill.points[i][0], fill.points[i][1]);
        }
        ctx.closePath();
        ctx.fill();
      }
      // In debug mode, don't fill corners - let the wall colors show through
    }
  });

  ctx.restore();

  return wallNormals;
};

/**
 * Draws wall normal arrows and thickness labels
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Array} wallNormals - Array of wall normal data
 * @param {number} scale - Current zoom scale
 */
export const drawWallNormals = (
  ctx,
  wallNormals,
  scale,
  debugMode = false,
  colorOverride = null
) => {
  const normalLength = 0.3; // 30cm arrow
  const arrowHeadLength = 0.1; // 10cm arrowhead

  wallNormals.forEach(
    ({ centerX, centerZ, perpDir, thickness, alignmentStatus }) => {
      // Arrow shaft endpoints
      const arrowStartX = centerX;
      const arrowStartZ = centerZ;
      const arrowEndX = centerX + perpDir[0] * normalLength;
      const arrowEndZ = centerZ + perpDir[1] * normalLength;

      // Set arrow color. `colorOverride` is used by the exterior-wall debug
      // pass (purple) — interior room walls use the alignment-status palette.
      const arrowColor =
        colorOverride ??
        (debugMode
          ? alignmentStatus === 'unmatched'
            ? '#FF0000' // Red for unmatched walls
            : alignmentStatus === 'unrefined'
              ? '#FFA500' // Orange for unrefined walls
              : '#00ff00' // Green for normal/aligned walls
          : '#00ff00'); // Always green in non-debug mode

      // Draw arrow shaft
      ctx.beginPath();
      ctx.moveTo(arrowStartX, arrowStartZ);
      ctx.lineTo(arrowEndX, arrowEndZ);
      ctx.strokeStyle = arrowColor;
      ctx.lineWidth = Math.max(0.01, 2 / scale);
      ctx.stroke();

      // Draw arrowhead
      const arrowAngle = Math.atan2(perpDir[1], perpDir[0]);
      const arrowHead1X =
        arrowEndX - arrowHeadLength * Math.cos(arrowAngle - Math.PI / 6);
      const arrowHead1Z =
        arrowEndZ - arrowHeadLength * Math.sin(arrowAngle - Math.PI / 6);
      const arrowHead2X =
        arrowEndX - arrowHeadLength * Math.cos(arrowAngle + Math.PI / 6);
      const arrowHead2Z =
        arrowEndZ - arrowHeadLength * Math.sin(arrowAngle + Math.PI / 6);

      ctx.beginPath();
      ctx.moveTo(arrowEndX, arrowEndZ);
      ctx.lineTo(arrowHead1X, arrowHead1Z);
      ctx.moveTo(arrowEndX, arrowEndZ);
      ctx.lineTo(arrowHead2X, arrowHead2Z);
      ctx.stroke();
    }
  );
};

/**
 * Draws room polygon outline for visualization
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} room - Room object
 * @param {Array} points - Polygon corner points
 * @param {number} scale - Current zoom scale
 */
/**
 * Debug-only: fill light pink under each room's persisted ceiling
 * facets / gap-fills (skip bridges — they're vertical and have a
 * degenerate XZ footprint). Run BEFORE wall + outline draws so the
 * tint sits under the rest. Areas of the floor polygon without a
 * pink overlay are rooms / sub-regions where the ceiling is missing.
 */
export const drawCeilingCoverage = (ctx, ceiling, scale) => {
  if (!Array.isArray(ceiling) || ceiling.length === 0) return;
  ctx.save();
  ctx.fillStyle = 'rgba(255, 182, 193, 0.35)'; // light pink, semi-transparent
  ctx.strokeStyle = 'rgba(255, 105, 140, 0.6)';
  ctx.lineWidth = Math.max(0.01, 1 / scale);
  for (const entry of ceiling) {
    if (!entry || entry.generatedFrom === 'bridge') continue;
    const corners = entry.polygonCorners;
    if (!Array.isArray(corners) || corners.length < 3) continue;
    ctx.beginPath();
    let started = false;
    for (const c of corners) {
      const x = Array.isArray(c) ? c[0] : c?.x;
      const z = Array.isArray(c) ? c[1] : c?.z;
      if (x == null || z == null) continue;
      if (!started) {
        ctx.moveTo(x, z);
        started = true;
      } else {
        ctx.lineTo(x, z);
      }
    }
    if (started) {
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
  }
  ctx.restore();
};

export const drawRoomPolygonOutline = (ctx, room, points, scale) => {
  // For new data format, polygon points should already be in world coordinates
  let finalWorldPoints = points.map((point) => {
    if (point.x !== undefined && point.z !== undefined) {
      return [point.x, point.z]; // Use x and z for 2D floor plan
    }
    // Fallback for old format
    return [point[0], point[1]];
  });

  // Draw polygon outline
  ctx.save();
  ctx.strokeStyle = HIGHLIGHT_COLORS.WALL_SELECTED; // Red outline
  ctx.lineWidth = Math.max(0.02, 2 / scale);
  ctx.setLineDash([Math.max(0.1, 5 / scale), Math.max(0.1, 5 / scale)]);

  ctx.beginPath();
  ctx.moveTo(finalWorldPoints[0][0], finalWorldPoints[0][1]);
  for (let i = 1; i < finalWorldPoints.length; i++) {
    ctx.lineTo(finalWorldPoints[i][0], finalWorldPoints[i][1]);
  }
  ctx.closePath();
  ctx.stroke();

  ctx.restore();
};

/**
 * Draws walls from room polygon with mitered corners
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} room - Room object
 * @param {Array} points - Polygon corner points
 * @param {number} scale - Current zoom scale
 */
export const drawWallsFromRoomPolygon = (ctx, room, points, scale) => {
  // For new data format, polygon points should already be in world coordinates
  let finalWorldPoints = points.map((point) => {
    if (point.x !== undefined && point.z !== undefined) {
      return [point.x, point.z]; // Use x and z for 2D floor plan
    }
    // Fallback for old format
    return [point[0], point[1]];
  });

  const wallThickness = 0.15;

  ctx.fillStyle = ELEMENT_COLORS.WALL_BLACK;
  ctx.strokeStyle = ELEMENT_COLORS.WALL_BLACK;
  ctx.lineWidth = Math.max(0.02, 2 / scale);

  // Calculate all interior and exterior corner points with mitering
  const interiorPoints = finalWorldPoints;
  const exteriorPoints = [];

  for (let i = 0; i < finalWorldPoints.length; i++) {
    const prevIndex =
      (i - 1 + finalWorldPoints.length) % finalWorldPoints.length;
    const currentIndex = i;
    const nextIndex = (i + 1) % finalWorldPoints.length;

    const prevPoint = finalWorldPoints[prevIndex];
    const currentPoint = finalWorldPoints[currentIndex];
    const nextPoint = finalWorldPoints[nextIndex];

    const exteriorPoint = calculateMiterPoint(
      prevPoint,
      currentPoint,
      nextPoint,
      wallThickness
    );
    exteriorPoints.push(exteriorPoint);
  }

  // Draw the wall as a single polygon
  ctx.beginPath();

  // Draw interior polygon (original room boundary)
  ctx.moveTo(interiorPoints[0][0], interiorPoints[0][1]);
  for (let i = 1; i < interiorPoints.length; i++) {
    ctx.lineTo(interiorPoints[i][0], interiorPoints[i][1]);
  }
  ctx.closePath();

  // Draw exterior polygon (mitered outer boundary)
  ctx.moveTo(exteriorPoints[0][0], exteriorPoints[0][1]);
  for (let i = 1; i < exteriorPoints.length; i++) {
    ctx.lineTo(exteriorPoints[i][0], exteriorPoints[i][1]);
  }
  ctx.closePath();

  // Use even-odd fill rule to create the wall thickness
  ctx.fill('evenodd');
};

/**
 * Draws windows
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Array} windows - Array of window objects
 * @param {number} scale - Current zoom scale
 * @param {Object} selectedItem - Currently selected item
 * @param {boolean} debugMode - Whether in debug mode
 */
/**
 * Helper function to check if two windows overlap on the same wall
 * @param {Object} window1 - First window
 * @param {Object} window2 - Second window
 * @param {Array} walls - Array of walls
 * @returns {boolean} - True if windows overlap
 */
const checkWindowOverlap = (window1, window2, walls) => {
  if (
    !window1.center ||
    !window2.center ||
    !window1.dimensions ||
    !window2.dimensions
  ) {
    return false;
  }

  // Find parent walls
  const wall1 = findParentWallForWindow(window1, walls);
  const wall2 = findParentWallForWindow(window2, walls);

  // Only check if on same wall
  if (!wall1 || !wall2 || wall1 !== wall2) {
    return false;
  }

  // Get wall direction
  const wallDir = apply3DRotationTo2D(wall1.rotation);
  const wallLength = wall1.dimensions?.x || 10;

  // Project windows onto wall axis
  const wall1Start = wall1.center.x - (wallDir[0] * wallLength) / 2;
  const wall1StartZ = wall1.center.z - (wallDir[1] * wallLength) / 2;

  // Calculate positions along wall for both windows
  const w1Offset =
    (window1.center.x - wall1Start) * wallDir[0] +
    (window1.center.z - wall1StartZ) * wallDir[1];
  const w2Offset =
    (window2.center.x - wall1Start) * wallDir[0] +
    (window2.center.z - wall1StartZ) * wallDir[1];

  const w1Left = w1Offset - window1.dimensions.x / 2;
  const w1Right = w1Offset + window1.dimensions.x / 2;
  const w2Left = w2Offset - window2.dimensions.x / 2;
  const w2Right = w2Offset + window2.dimensions.x / 2;

  // Check overlap with small tolerance
  const tolerance = 0.01; // 1cm
  return w1Right > w2Left + tolerance && w1Left < w2Right - tolerance;
};

/**
 * Helper function to find parent wall for a window
 * @param {Object} window - Window object
 * @param {Array} walls - Array of walls
 * @returns {Object|null} - Parent wall or null
 */
const findParentWallForWindow = (window, walls) => {
  if (!window || !walls || walls.length === 0) return null;

  // First try parent wall IDs
  if (window.parentWallIds?.[0]) {
    const parentWall = walls.find(
      (wall) => wall.id === window.parentWallIds[0]
    );
    if (parentWall) return parentWall;
  }

  // Fallback to closest wall
  let closestWall = null;
  let minDistance = Infinity;

  walls.forEach((wall) => {
    if (!wall.center) return;

    const distance = Math.sqrt(
      Math.pow(window.center.x - wall.center.x, 2) +
        Math.pow(window.center.z - wall.center.z, 2)
    );

    if (distance < minDistance && distance < 1.0) {
      minDistance = distance;
      closestWall = wall;
    }
  });

  return closestWall;
};

export const drawWindows = (
  ctx,
  windows,
  scale,
  selectedItem,
  debugMode = false,
  walls = [],
  doors = [],
  showRoomWallsBold = false,
  structureWalls = [],
  highlightObjectIds = null, // Set<string> — render with PRIMARY highlight
  secondaryHighlightObjectIds = null, // Set<string> — render with SECONDARY highlight
  inFocusObjectIds = null // Set<string> — non-focus windows render at 0.5 alpha
) => {
  // Filter out windows that might be at door locations
  const filteredWindows = windows.filter((window) => {
    // Skip invalid windows
    if (!window || !window.center || !window.dimensions) {
      console.warn('Invalid window data:', window);
      return false;
    }

    // Check if this window is at a door location
    const isAtDoorLocation = doors.some((door) => {
      if (!door.center || !window.center) return false;
      const distance = Math.sqrt(
        Math.pow(window.center.x - door.center.x, 2) +
          Math.pow(window.center.z - door.center.z, 2)
      );
      return distance < 0.1; // Within 10cm
    });
    return !isAtDoorLocation;
  });

  // First pass: detect collisions
  // Combine room walls and structure walls so parentWallIds (which may
  // reference structure wall IDs like sw_*) can be resolved correctly
  const allWallsForCollision =
    structureWalls && structureWalls.length > 0
      ? [...walls, ...structureWalls]
      : walls;

  const windowsWithCollisions = new Set();

  // Check each window against others
  filteredWindows.forEach((window1, i) => {
    filteredWindows.forEach((window2, j) => {
      if (i >= j) return; // Avoid duplicate checks

      if (checkWindowOverlap(window1, window2, allWallsForCollision)) {
        windowsWithCollisions.add(window1.id);
        windowsWithCollisions.add(window2.id);
      }
    });
  });

  // Draw windows
  filteredWindows.forEach((window) => {
    if (
      !window.center ||
      window.center.x === undefined ||
      window.center.z === undefined ||
      !window.dimensions
    )
      return;

    // Window properties
    const halfWidth = window.dimensions.x / 2;

    // Apply full 3D rotation to get window direction
    let normalizedDir = [1, 0]; // Default direction
    try {
      if (window.rotation) {
        normalizedDir = apply3DRotationTo2D(window.rotation);
      } else {
        console.warn('Window missing rotation:', window.id);
      }
    } catch (error) {
      console.error('Error calculating window rotation:', error, window);
      return; // Skip this window
    }

    // Calculate the window position using center
    const startX = window.center.x - normalizedDir[0] * halfWidth;
    const startZ = window.center.z - normalizedDir[1] * halfWidth;
    const endX = window.center.x + normalizedDir[0] * halfWidth;
    const endZ = window.center.z + normalizedDir[1] * halfWidth;

    // Check if this window is selected - need exact match
    const windowId = window.id || null;
    const selectedId = selectedItem?.id || null;

    // Check for exact match by ID first
    let isSelected =
      selectedItem &&
      selectedItem.category?.window &&
      matchesOpeningSelection(window, selectedItem);

    // If no ID match but we have a selected window, check by position as fallback
    if (
      !isSelected &&
      selectedItem &&
      selectedItem.category?.window &&
      window.center &&
      selectedItem.center
    ) {
      const centerMatch =
        Math.abs(window.center.x - selectedItem.center.x) < 0.01 &&
        Math.abs(window.center.z - selectedItem.center.z) < 0.01;
      if (centerMatch) {
        isSelected = true;
        console.log('Window matched by position:', {
          windowCenter: window.center,
          selectedCenter: selectedItem.center,
        });
      }
    }

    // Measurement-driven highlights: primary first, then secondary as a
    // softer tint via selectionStrokeColor.
    let isSecondaryHighlight = false;
    if (
      !isSelected &&
      highlightObjectIds &&
      windowId &&
      highlightObjectIds.has(windowId)
    ) {
      isSelected = true;
    } else if (
      !isSelected &&
      secondaryHighlightObjectIds &&
      windowId &&
      secondaryHighlightObjectIds.has(windowId)
    ) {
      isSelected = true;
      isSecondaryHighlight = true;
    }
    const selectionStrokeColor = isSecondaryHighlight
      ? HIGHLIGHT_COLORS.SECONDARY
      : HIGHLIGHT_COLORS.PRIMARY;

    // Dim non-focus windows to 0.5 alpha when something is in focus.
    const dim = inFocusObjectIds && windowId && !inFocusObjectIds.has(windowId);
    if (dim) {
      ctx.save();
      ctx.globalAlpha *= 0.5;
    }

    // Remove debug logging to prevent console spam

    // Check if window has collision flag or was detected as overlapping
    const hasCollision =
      window.hasCollision === true || windowsWithCollisions.has(windowId);

    // Window should be drawn as three lines that span the wall thickness
    const lineColor = isSelected ? selectionStrokeColor : ELEMENT_COLORS.WINDOW;
    const lineWidth = isSelected
      ? HIGHLIGHT_COLORS.MEDIUM_LINE(scale)
      : Math.max(0.01, 1.5 / scale);

    // Match wall thickness and behavior
    const actualThickness = window.dimensions?.z;
    const wallThickness = debugMode
      ? actualThickness || 0.0127 // Debug: actual or 0.5 inches
      : actualThickness || 0.1524; // Non-debug: actual or 6 inches

    // Get perpendicular direction (pointing outward from wall)
    const perpDir = [-normalizedDir[1], normalizedDir[0]];

    // Calculate the three line positions
    let lineOffsets;
    let boldWindowThickness = 0.05; // Default 5cm thickness for bold mode

    // In bold mode, try to get the structure wall thickness
    if (showRoomWallsBold && window.parentWallIds?.length > 0) {
      const parentWallId = window.parentWallIds[0];
      // Direct lookup: parentWallIds may already reference a structure wall
      const structureWall = structureWalls.find((sw) => sw.id === parentWallId);
      if (structureWall?.dimensions?.z) {
        boldWindowThickness = structureWall.dimensions.z;
      } else {
        // Indirect lookup: room wall → alignment → structure wall
        const parentRoomWall = walls.find((w) => w.id === parentWallId);
        if (parentRoomWall?.alignment?.structureWallId) {
          const sw = structureWalls.find(
            (s) => s.id === parentRoomWall.alignment.structureWallId
          );
          if (sw?.dimensions?.z) {
            boldWindowThickness = sw.dimensions.z;
          }
        }
      }
    }

    if (showRoomWallsBold) {
      // Bold room walls mode: draw window centered at its actual position
      const halfThickness = boldWindowThickness / 2;
      lineOffsets = [-halfThickness, 0, halfThickness];
    } else if (!debugMode && !actualThickness) {
      // In non-debug mode without actual thickness, wall extends outward only
      // So window lines should be at: centerline (0), middle of wall, and outer edge
      lineOffsets = [0, -wallThickness / 2, -wallThickness];
    } else {
      // In debug mode or with actual thickness, wall is centered
      const halfThickness = wallThickness / 2;
      lineOffsets = [-halfThickness, 0, halfThickness];
    }

    // Draw white background first for better visibility
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = Math.max(0.02, 3 / scale);

    lineOffsets.forEach((offset) => {
      const offsetX = perpDir[0] * offset;
      const offsetZ = perpDir[1] * offset;

      ctx.beginPath();
      ctx.moveTo(startX + offsetX, startZ + offsetZ);
      ctx.lineTo(endX + offsetX, endZ + offsetZ);
      ctx.stroke();
    });

    // Draw the three thin lines
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = lineWidth;

    lineOffsets.forEach((offset) => {
      const offsetX = perpDir[0] * offset;
      const offsetZ = perpDir[1] * offset;

      ctx.beginPath();
      ctx.moveTo(startX + offsetX, startZ + offsetZ);
      ctx.lineTo(endX + offsetX, endZ + offsetZ);
      ctx.stroke();
    });

    // In bold mode, draw frame rectangles at each end
    if (showRoomWallsBold) {
      const frameWidth = 0.06; // 6cm frame width along window direction
      const frameThickness = boldWindowThickness; // Same as window thickness

      ctx.fillStyle = lineColor;

      // Frame at start end
      const startFrameCorners = [
        [
          startX - perpDir[0] * (frameThickness / 2),
          startZ - perpDir[1] * (frameThickness / 2),
        ],
        [
          startX + perpDir[0] * (frameThickness / 2),
          startZ + perpDir[1] * (frameThickness / 2),
        ],
        [
          startX +
            normalizedDir[0] * frameWidth +
            perpDir[0] * (frameThickness / 2),
          startZ +
            normalizedDir[1] * frameWidth +
            perpDir[1] * (frameThickness / 2),
        ],
        [
          startX +
            normalizedDir[0] * frameWidth -
            perpDir[0] * (frameThickness / 2),
          startZ +
            normalizedDir[1] * frameWidth -
            perpDir[1] * (frameThickness / 2),
        ],
      ];

      ctx.beginPath();
      ctx.moveTo(startFrameCorners[0][0], startFrameCorners[0][1]);
      for (let i = 1; i < startFrameCorners.length; i++) {
        ctx.lineTo(startFrameCorners[i][0], startFrameCorners[i][1]);
      }
      ctx.closePath();
      ctx.fill();

      // Frame at end
      const endFrameCorners = [
        [
          endX - perpDir[0] * (frameThickness / 2),
          endZ - perpDir[1] * (frameThickness / 2),
        ],
        [
          endX + perpDir[0] * (frameThickness / 2),
          endZ + perpDir[1] * (frameThickness / 2),
        ],
        [
          endX -
            normalizedDir[0] * frameWidth +
            perpDir[0] * (frameThickness / 2),
          endZ -
            normalizedDir[1] * frameWidth +
            perpDir[1] * (frameThickness / 2),
        ],
        [
          endX -
            normalizedDir[0] * frameWidth -
            perpDir[0] * (frameThickness / 2),
          endZ -
            normalizedDir[1] * frameWidth -
            perpDir[1] * (frameThickness / 2),
        ],
      ];

      ctx.beginPath();
      ctx.moveTo(endFrameCorners[0][0], endFrameCorners[0][1]);
      for (let i = 1; i < endFrameCorners.length; i++) {
        ctx.lineTo(endFrameCorners[i][0], endFrameCorners[i][1]);
      }
      ctx.closePath();
      ctx.fill();
    }

    // No additional highlight box - just the red lines are enough

    // Draw additional collision indicators if window has collision
    if (hasCollision) {
      ctx.save();

      // Draw diagonal lines between the existing window lines
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = Math.max(0.008, 1.5 / scale);
      ctx.setLineDash([0.03, 0.03]); // Small dashes

      // Draw X pattern connecting the three lines
      // Connect first line to third line
      lineOffsets.forEach((offset1, i) => {
        if (i < lineOffsets.length - 1) {
          const offset2 = lineOffsets[i + 1];

          // Diagonal from start of line i to end of line i+1
          ctx.beginPath();
          ctx.moveTo(
            startX + perpDir[0] * offset1,
            startZ + perpDir[1] * offset1
          );
          ctx.lineTo(endX + perpDir[0] * offset2, endZ + perpDir[1] * offset2);
          ctx.stroke();

          // Diagonal from end of line i to start of line i+1
          ctx.beginPath();
          ctx.moveTo(endX + perpDir[0] * offset1, endZ + perpDir[1] * offset1);
          ctx.lineTo(
            startX + perpDir[0] * offset2,
            startZ + perpDir[1] * offset2
          );
          ctx.stroke();
        }
      });

      ctx.restore();
    }

    if (dim) ctx.restore();
  });
};

/**
 * Helper function to find parent wall for a door
 * @param {Object} door - Door object
 * @param {Array} walls - Array of wall objects
 * @returns {Object|null} - Parent wall or null if not found
 */
const findParentWallForDoor = (door, walls) => {
  if (!door || !walls || walls.length === 0) return null;

  // First try to match by parent wall IDs
  if (door.parentWallIds?.[0]) {
    const parentWall = walls.find((wall) => wall.id === door.parentWallIds[0]);
    if (parentWall) return parentWall;
  }

  // Fallback: find closest wall by distance. Room-copy doors carry no
  // `center` (only structure copies do), so distance-matching is impossible.
  // This happens transiently during a checkpoint restore — the rooms (with
  // their door copies) arrive via the live subscription a frame before the
  // refetched structure copies — so skip rather than crash; the door renders
  // on the next frame once the structure data settles.
  if (!door.center) return null;

  let closestWall = null;
  let minDistance = Infinity;

  walls.forEach((wall) => {
    if (!wall.center) return;

    // Calculate distance from door center to wall center
    const distance = Math.sqrt(
      Math.pow(door.center.x - wall.center.x, 2) +
        Math.pow(door.center.z - wall.center.z, 2)
    );

    // Check if this is the closest wall and within reasonable distance (1 meter)
    if (distance < minDistance && distance < 1.0) {
      minDistance = distance;
      closestWall = wall;
    }
  });

  return closestWall;
};

/**
 * Draws doors with rotation, swing arcs, and labels
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Array} doors - Array of door objects
 * @param {number} scale - Current zoom scale
 * @param {Object} selectedItem - Currently selected item
 * @param {boolean} debugMode - Debug mode flag
 * @param {Array} walls - Array of wall objects for boundary checking
 */
export const drawDoorsWithRotation = (
  ctx,
  doors,
  scale,
  selectedItem,
  debugMode = false,
  walls = [],
  showRoomWallsBold = false,
  structureWalls = [],
  highlightObjectIds = null, // Set<string> — render with PRIMARY highlight
  secondaryHighlightObjectIds = null, // Set<string> — render with SECONDARY highlight
  inFocusObjectIds = null // Set<string> — non-focus doors render at 0.5 alpha
) => {
  // First, group doors by their parent wall
  const doorsByWall = new Map();
  doors.forEach((door) => {
    const parentWall = findParentWallForDoor(door, walls);
    if (parentWall) {
      const wallId = parentWall.id;
      if (!doorsByWall.has(wallId)) {
        doorsByWall.set(wallId, []);
      }
      doorsByWall.get(wallId).push(door);
    }
  });
  doors.forEach((door) => {
    if (
      !door.center ||
      door.center.x === undefined ||
      door.center.z === undefined ||
      !door.dimensions
    )
      return;

    // Door properties
    const halfWidth = door.dimensions.x / 2;
    const doorThickness = door.dimensions.z || 0.05; // 5cm default thickness

    // Apply full 3D rotation to get door direction
    const normalizedDir = apply3DRotationTo2D(door.rotation);

    // Get perpendicular direction (for door thickness)
    // Note: This gives us the direction perpendicular to the door
    const perpDir = [-normalizedDir[1], normalizedDir[0]];

    // Calculate door endpoints
    const startX = door.center.x - normalizedDir[0] * halfWidth;
    const startZ = door.center.z - normalizedDir[1] * halfWidth;
    const endX = door.center.x + normalizedDir[0] * halfWidth;
    const endZ = door.center.z + normalizedDir[1] * halfWidth;

    // Check if this door is selected. Measurement-driven highlights fold
    // into the same flag — real selection state lives in selectedItem,
    // but visually the door renders the same either way. Secondary tier
    // shifts the stroke color (see selectionStrokeColor below).
    const isReallySelected = matchesOpeningSelection(door, selectedItem);
    const isPrimaryHighlight =
      !isReallySelected &&
      highlightObjectIds &&
      door.id &&
      highlightObjectIds.has(door.id);
    const isSecondaryHighlight =
      !isReallySelected &&
      !isPrimaryHighlight &&
      secondaryHighlightObjectIds &&
      door.id &&
      secondaryHighlightObjectIds.has(door.id);
    const isSelected =
      isReallySelected || isPrimaryHighlight || isSecondaryHighlight;
    const selectionStrokeColor = isSecondaryHighlight
      ? HIGHLIGHT_COLORS.SECONDARY
      : HIGHLIGHT_COLORS.PRIMARY;

    // Dim non-focus doors to 0.5 alpha when something is in focus.
    const dim = inFocusObjectIds && door.id && !inFocusObjectIds.has(door.id);
    if (dim) {
      ctx.save();
      ctx.globalAlpha *= 0.5;
    }

    // Get door type (single source of truth)
    const doorType = door.category?.door?.doorType;

    // Debug logging for selected doors only
    if (isSelected && debugMode) {
      console.log('Drawing door:', door.id, {
        doorType,
        category: door.category,
      });
    }

    // Handle different door types
    if (isSlidingDoor(doorType)) {
      // Sliding door visualization with multi-panel support
      const isRightHandle =
        doorType?.includes('right') || doorType?.includes('Right');
      const slideDirection = isRightHandle ? 1 : -1;
      const slidingPanelCount = door.category?.door?.slidingPanelCount || 1;

      // Find parent wall
      const parentWallId = door.parentWallIds?.[0];
      const parentWall = walls?.find((wall) => wall.id === parentWallId);

      // Calculate panel dimensions
      const doorWidth = door.dimensions.x;
      const doorPanelThickness = 0.0381; // 1.5 inches
      const gapBetweenPanels = 0.0127; // 0.5 inch

      // Calculate individual panel width
      // Each panel should be sized to cover its portion of the door opening
      const panelWidth = doorWidth / slidingPanelCount;
      const overlap = slidingPanelCount > 1 ? 0.0762 : 0; // 3 inches overlap for visual indication only

      // Draw panels in closed position
      // Get perpendicular direction (pointing into the wall)
      const perpDir = [-normalizedDir[1], normalizedDir[0]];

      // Stack panels with proper spacing and show appropriate panel partially open
      // For left handle sliding door, rightmost panel opens first
      // For right handle sliding door, leftmost panel opens first
      // NOTE: If coordinate system is flipped, panel 0 might be rightmost
      const openingPanelIndex = isRightHandle ? slidingPanelCount - 1 : 0;
      // For proper visualization, the opening panel needs to slide enough to show a gap with the wall
      // For a 3-panel door, the rightmost panel needs to slide left by more than just its width
      const slideAmount = panelWidth * 0.2; // 20% of panel width to show opening

      // Calculate staggering (perpendicular offset)
      const wallThickness = 0.1524; // 6 inches

      for (let i = 0; i < slidingPanelCount; i++) {
        // Calculate perpendicular offset (staggering)
        // Panels should be centered within the wall thickness

        // Calculate perpendicular stacking: panel 0 at the inner edge,
        // successive panels offset outward. Each panel is offset by just
        // the gap between panels so the stack stays within the wall.
        const totalStackDepth = (slidingPanelCount - 1) * gapBetweenPanels;
        let stackOffset = totalStackDepth / 2 - i * gapBetweenPanels;

        // Calculate panel position along door
        let panelStartOffset, panelEndOffset;

        // Calculate base position for each panel
        const basePosition = -doorWidth / 2 + i * panelWidth;

        // Debug logging
        if (debugMode && i === 0) {
          // console.log('Door coordinate system:', {
          //   doorType,
          //   doorWidth,
          //   panelWidth,
          //   normalizedDir,
          //   panel0_basePos: basePosition,
          //   panel0_worldStart: door.center.x + normalizedDir[0] * basePosition,
          //   door_center: door.center,
          // });
        }

        // Calculate panel positions
        panelStartOffset = basePosition;
        panelEndOffset = basePosition + panelWidth;

        // Apply sliding logic - panels should overlap when sliding
        if (!isRightHandle) {
          // For left handle sliding, only the rightmost panel slides
          // It slides BEHIND the other panels to create gap on right
          if (i === openingPanelIndex) {
            // This is panel 0 (which appears rightmost due to flipped coordinates)
            // If coordinates are flipped, sliding "left" means positive direction
            panelStartOffset = basePosition + slideAmount * 3; // Slide significantly to overlap
            panelEndOffset = panelStartOffset + panelWidth;
          }
          // Other panels stay in their original positions
        } else {
          // For right handle sliding, only the leftmost panel slides
          // It slides BEHIND the other panels to create gap on left
          if (i === openingPanelIndex) {
            // This is panel 2 for right sliding - slide it left to overlap
            // Use same slide amount as left sliding for consistency
            panelStartOffset = basePosition - slideAmount * 3; // Slide significantly to overlap
            panelEndOffset = panelStartOffset + panelWidth;
          }
          // Other panels stay in their original positions
        }

        if (debugMode) {
          // console.log(`Panel ${i}:`, {
          //   isOpening: i === openingPanelIndex,
          //   basePosition,
          //   panelStartOffset,
          //   panelEndOffset,
          // });
        }

        // Panel start and end points
        const panelStartX =
          door.center.x +
          normalizedDir[0] * panelStartOffset +
          perpDir[0] * stackOffset;
        const panelStartZ =
          door.center.z +
          normalizedDir[1] * panelStartOffset +
          perpDir[1] * stackOffset;
        const panelEndX =
          door.center.x +
          normalizedDir[0] * panelEndOffset +
          perpDir[0] * stackOffset;
        const panelEndZ =
          door.center.z +
          normalizedDir[1] * panelEndOffset +
          perpDir[1] * stackOffset;

        // Draw panel outline
        // Use consistent colors - blue for unselected, pink for selected
        ctx.strokeStyle = isSelected ? selectionStrokeColor : '#0066cc'; // Pink when selected, blue otherwise

        // Use consistent thickness for all panels - slightly thicker than before
        ctx.lineWidth = 0.0508; // 2 inches - thicker and consistent for all panels

        // Draw panel
        ctx.beginPath();
        ctx.moveTo(panelStartX, panelStartZ);
        ctx.lineTo(panelEndX, panelEndZ);
        ctx.stroke();

        // Add panel number only in debug mode
        if (debugMode) {
          const panelMidX = (panelStartX + panelEndX) / 2;
          const panelMidZ = (panelStartZ + panelEndZ) / 2;
          ctx.save();
          ctx.font = `${Math.max(0.1, 2 / scale)}px Arial`;
          ctx.fillStyle = i === openingPanelIndex ? '#ff0000' : '#000000';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(`${i}`, panelMidX, panelMidZ);
          ctx.restore();
        }
      }

      // Direction is now indicated by the first panel being partially open
    } else if (isPocketDoor(doorType)) {
      // Pocket door visualization
      const isRightHandlePocket =
        doorType?.includes('right') || doorType?.includes('Right');

      // Debug log for large door
      if (door.id === 'FCCD75CD-9A3B-4569-85D3-8661A7E580FC') {
        console.log('DEBUG: Rendering large pocket door', {
          doorType,
          width: door.dimensions.x,
          thickness: door.dimensions.z || 0.05,
          center: door.center,
        });
      }

      // Skip drawing the door line here - it will be drawn by the wall gap
      // We only need to draw the pocket cavity and open position

      const pocketDirection = isRightHandlePocket ? -1 : 1;

      // Calculate pocket dimensions
      const doorWidth = door.dimensions.x;
      const pocketLength = doorWidth; // Make pocket same length as door
      const gapFromDoor = 0.02; // 2cm gap between door and pocket start

      // Simple check: if pocket length > wall length, show red
      let pocketColor = '#FFFFFF'; // Default white

      // Find parent wall to check boundaries
      const parentWall = findParentWallForDoor(door, walls);

      if (parentWall && parentWall.dimensions) {
        const wallLength = parentWall.dimensions.x || 0;
        const wallId = parentWall.id;

        // Check if there are other doors on the same wall
        const doorsOnSameWall = doorsByWall.get(wallId) || [];
        let availableSpace = wallLength; // Default to full wall length

        if (doorsOnSameWall.length > 1) {
          // Find adjacent doors and calculate available space
          const wallDir = apply3DRotationTo2D(parentWall.rotation);

          // Calculate position along wall for each door
          const doorPositions = doorsOnSameWall.map((d) => {
            const doorVectorX = d.center.x - parentWall.center.x;
            const doorVectorZ = d.center.z - parentWall.center.z;
            const positionAlongWall =
              doorVectorX * wallDir[0] + doorVectorZ * wallDir[1];
            return {
              door: d,
              position: positionAlongWall,
              leftEdge: positionAlongWall - d.dimensions.x / 2,
              rightEdge: positionAlongWall + d.dimensions.x / 2,
            };
          });

          // Sort by position
          doorPositions.sort((a, b) => a.position - b.position);

          // Find current door in the list
          const currentDoorIndex = doorPositions.findIndex(
            (dp) => dp.door === door
          );

          if (currentDoorIndex >= 0) {
            // For right handle pocket, check space to the left
            // For left handle pocket, check space to the right
            if (isRightHandlePocket && currentDoorIndex > 0) {
              // Check space between this door and the door to its left
              const leftDoor = doorPositions[currentDoorIndex - 1];
              availableSpace =
                doorPositions[currentDoorIndex].leftEdge - leftDoor.rightEdge;
              // console.log(
              //   'Space to left door:',
              //   availableSpace.toFixed(2) + 'm'
              // );
            } else if (
              !isRightHandlePocket &&
              currentDoorIndex < doorPositions.length - 1
            ) {
              // Check space between this door and the door to its right
              const rightDoor = doorPositions[currentDoorIndex + 1];
              availableSpace =
                rightDoor.leftEdge - doorPositions[currentDoorIndex].rightEdge;
              console.log(
                'Space to right door:',
                availableSpace.toFixed(2) + 'm'
              );
            }
          }
        }

        // The total visual extent is door width + gap + pocket length
        const totalVisualExtent = doorWidth + gapFromDoor + pocketLength;

        // console.log('Pocket space check:', {
        //   doorId: door.id,
        //   doorWidth: doorWidth.toFixed(2),
        //   pocketLength: pocketLength.toFixed(2),
        //   totalVisualExtent: totalVisualExtent.toFixed(2),
        //   availableSpace: availableSpace.toFixed(2),
        //   doorsOnWall: doorsOnSameWall.length,
        //   fitsInSpace: totalVisualExtent <= availableSpace,
        // });

        // Check if total visual extent is longer than available space
        if (totalVisualExtent > availableSpace) {
          // console.warn(
          //   `Warning: Pocket door ${door.id} needs ${totalVisualExtent.toFixed(2)}m but only ${availableSpace.toFixed(2)}m available!`
          // );
          pocketColor = '#FFB0B0'; // Light red for warning
        }
      } else {
        // No parent wall found - show warning
        // console.warn(
        //   `Warning: Cannot find parent wall for door ${door.id}`
        // );
      }

      // Calculate pocket position with gap from door
      let pocketStartX, pocketStartZ;
      if (pocketDirection > 0) {
        // Left pocket: extends from right end of door
        // Add a smaller gap (2cm) for visual separation
        pocketStartX = endX + normalizedDir[0] * 0.02;
        pocketStartZ = endZ + normalizedDir[1] * 0.02;
      } else {
        // Right pocket: extends from left end of door
        // Add a smaller gap (2cm) for visual separation
        pocketStartX = startX - normalizedDir[0] * 0.02;
        pocketStartZ = startZ - normalizedDir[1] * 0.02;
      }

      // Pocket end is further along in the pocket direction
      const pocketEndX =
        pocketStartX + normalizedDir[0] * pocketLength * pocketDirection;
      const pocketEndZ =
        pocketStartZ + normalizedDir[1] * pocketLength * pocketDirection;

      // Debug: log actual pocket dimensions
      const actualPocketLength = Math.sqrt(
        Math.pow(pocketEndX - pocketStartX, 2) +
          Math.pow(pocketEndZ - pocketStartZ, 2)
      );
      // console.log('Pocket visual dimensions:', {
      //   pocketStartX: pocketStartX.toFixed(2),
      //   pocketStartZ: pocketStartZ.toFixed(2),
      //   pocketEndX: pocketEndX.toFixed(2),
      //   pocketEndZ: pocketEndZ.toFixed(2),
      //   actualPocketLength: actualPocketLength.toFixed(2),
      //   doorStartX: startX.toFixed(2),
      //   doorEndX: endX.toFixed(2),
      //   totalVisualExtent: Math.abs(
      //     pocketEndX - (doorType === 'rightPocket' ? endX : startX)
      //   ).toFixed(2),
      // });

      // Draw pocket walls (where door slides into)
      // Remove all the unused positioning code that's causing errors

      // Draw pocket cavity (where door slides into)
      ctx.save();

      // Use the pocket color determined above (white or light red for warning)
      // console.log('Setting pocket fill color to:', pocketColor);
      ctx.fillStyle = pocketColor;

      // Find best parent wall for pocket sizing — prefer structure wall
      // (it has real thickness and is what gets drawn as the bold wall)
      const structureParent =
        structureWalls.find((sw) => sw.id === door.parentWallIds?.[0]) ||
        findParentWallForDoor(door, structureWalls);
      const pocketWall = structureParent || parentWall;

      const actualWallThickness = pocketWall?.dimensions?.z;
      const wallThickness = debugMode
        ? actualWallThickness || 0.0127 // Debug: actual or 0.5 inches
        : actualWallThickness || 0.1524; // Non-debug: actual or 6 inches

      // Pocket width: just wide enough to fit a door panel (~1.5") plus clearance,
      // but never wider than the wall itself.
      const pocketHalfWidth = 0.0254; // 1" half-width = 2" total
      const pocketOffset = Math.min(pocketHalfWidth, wallThickness / 2);

      // Draw the pocket as a path to handle any wall orientation
      ctx.beginPath();

      // Center pocket on the parent wall's centerline.
      // Calculate perpendicular offset from door center to wall center
      // so the pocket aligns with the visually drawn wall.
      let centeringOffset = 0;
      if (pocketWall?.center) {
        const doorToWallX = pocketWall.center.x - door.center.x;
        const doorToWallZ = pocketWall.center.z - door.center.z;
        centeringOffset = doorToWallX * perpDir[0] + doorToWallZ * perpDir[1];
      } else if (!debugMode && !actualWallThickness) {
        // Wall extends outward only - center is at -wallThickness/2
        centeringOffset = -wallThickness / 2;
      }
      const p1X = pocketStartX + perpDir[0] * (centeringOffset + pocketOffset);
      const p1Z = pocketStartZ + perpDir[1] * (centeringOffset + pocketOffset);
      const p2X = pocketEndX + perpDir[0] * (centeringOffset + pocketOffset);
      const p2Z = pocketEndZ + perpDir[1] * (centeringOffset + pocketOffset);
      const p3X = pocketEndX + perpDir[0] * (centeringOffset - pocketOffset);
      const p3Z = pocketEndZ + perpDir[1] * (centeringOffset - pocketOffset);
      const p4X = pocketStartX + perpDir[0] * (centeringOffset - pocketOffset);
      const p4Z = pocketStartZ + perpDir[1] * (centeringOffset - pocketOffset);

      // Draw pocket rectangle
      ctx.moveTo(p1X, p1Z);
      ctx.lineTo(p2X, p2Z);
      ctx.lineTo(p3X, p3Z);
      ctx.lineTo(p4X, p4Z);
      ctx.closePath();
      ctx.fill();

      // Restore context state
      ctx.restore();
      // Debug visualization - show wall boundaries
      if (debugMode && parentWall) {
        ctx.save();
        ctx.setLineDash([]);
        ctx.lineWidth = Math.max(0.01, 1 / scale);

        // Draw wall extent markers
        const wallDir = apply3DRotationTo2D(parentWall.rotation);
        const wallStart = {
          x: parentWall.center.x - wallDir[0] * (parentWall.dimensions.x / 2),
          z: parentWall.center.z - wallDir[1] * (parentWall.dimensions.x / 2),
        };
        const wallEnd = {
          x: parentWall.center.x + wallDir[0] * (parentWall.dimensions.x / 2),
          z: parentWall.center.z + wallDir[1] * (parentWall.dimensions.x / 2),
        };

        // Draw wall extent in green
        ctx.strokeStyle = '#00FF00';
        ctx.beginPath();
        ctx.moveTo(wallStart.x, wallStart.z);
        ctx.lineTo(wallEnd.x, wallEnd.z);
        ctx.stroke();

        // Draw larger markers at wall ends
        ctx.fillStyle = '#00FF00';
        const markerSize = 0.1; // Larger markers
        ctx.fillRect(
          wallStart.x - markerSize / 2,
          wallStart.z - markerSize / 2,
          markerSize,
          markerSize
        );
        ctx.fillRect(
          wallEnd.x - markerSize / 2,
          wallEnd.z - markerSize / 2,
          markerSize,
          markerSize
        );

        // Add text labels
        ctx.save();
        ctx.scale(1 / scale, 1 / scale);
        ctx.font = '12px Arial';
        ctx.fillStyle = '#00FF00';
        ctx.fillText(
          'WALL START',
          wallStart.x * scale,
          wallStart.z * scale - 5
        );
        ctx.fillText('WALL END', wallEnd.x * scale, wallEnd.z * scale - 5);
        ctx.restore();

        // Draw pocket extent in red
        ctx.strokeStyle = '#FF0000';
        ctx.beginPath();
        ctx.moveTo(pocketStartX, pocketStartZ);
        ctx.lineTo(pocketEndX, pocketEndZ);
        ctx.stroke();

        ctx.restore();
      }

      ctx.setLineDash([]); // Reset dash

      // Draw the door in half-open position
      // For pocket doors, show the door halfway into the pocket
      const halfOpenOffset = pocketLength * 0.5; // Door is 50% open

      // Calculate door position when half open
      let doorStartX, doorStartZ, doorEndX, doorEndZ;
      if (isRightHandlePocket) {
        // Right handle pocket: door slides to the right
        doorStartX = startX - normalizedDir[0] * halfOpenOffset;
        doorStartZ = startZ - normalizedDir[1] * halfOpenOffset;
        doorEndX = endX - normalizedDir[0] * halfOpenOffset;
        doorEndZ = endZ - normalizedDir[1] * halfOpenOffset;
      } else {
        // Left handle pocket: door slides to the left
        doorStartX = startX + normalizedDir[0] * halfOpenOffset;
        doorStartZ = startZ + normalizedDir[1] * halfOpenOffset;
        doorEndX = endX + normalizedDir[0] * halfOpenOffset;
        doorEndZ = endZ + normalizedDir[1] * halfOpenOffset;
      }

      // Draw the half-open door as a black line with proper thickness
      ctx.save();

      // Draw door as a line of 1.5" thickness (0.0381m)
      const doorThickness = 0.0381; // 1.5 inches

      // Center the door line in the wall - must match pocket centering logic
      const doorCenteringOffset = centeringOffset; // Use same offset as pocket for consistency

      // Draw door as a thick blue line (similar to windows)
      ctx.strokeStyle = isSelected ? HIGHLIGHT_COLORS.PRIMARY : '#0066cc'; // Blue for door, pink/purple if selected
      ctx.lineWidth = doorThickness; // Use actual door thickness as line width
      ctx.lineCap = 'square'; // Square line caps for clean edges

      // Position door line in center of wall thickness (no offset needed for centering)
      const lineOffsetX = perpDir[0] * doorCenteringOffset;
      const lineOffsetZ = perpDir[1] * doorCenteringOffset;

      ctx.beginPath();
      ctx.moveTo(doorStartX + lineOffsetX, doorStartZ + lineOffsetZ);
      ctx.lineTo(doorEndX + lineOffsetX, doorEndZ + lineOffsetZ);
      ctx.stroke();

      ctx.restore();
    } else if (isFrenchDoor(doorType)) {
      // French doors - two symmetrical swing leaves meeting in the centre.
      // The geometry is computeDoorLeaves' (the one description of a
      // door's swing, shared with the 3D renderer, the debug page and the
      // CV swing matcher); only the stroke styling is local. This used to
      // re-derive hinges and angles inline, which is how the debug page
      // came to draw no French swing at all.
      const centerX = door.center.x;
      const centerZ = door.center.z;
      const leaves = computeDoorLeaves({
        centerX,
        centerZ,
        dir: normalizedDir,
        doorWidth: door.dimensions.x,
        doorType,
        wallFacesPriorityRoom: door.wallFacesPriorityRoom,
      });

      const strokeStyle = isSelected ? HIGHLIGHT_COLORS.PRIMARY : '#0066cc';
      // Inset by half stroke width so the arc's outer edge aligns with
      // the opening.
      const frenchArcLineWidth = isSelected
        ? HIGHLIGHT_COLORS.MEDIUM_LINE(scale)
        : HIGHLIGHT_COLORS.THIN_LINE(scale);
      const openLineWidth = isSelected
        ? HIGHLIGHT_COLORS.THICK_LINE(scale)
        : Math.max(0.02, 2 / scale);
      const radiusOf = (leaf) => leaf.leafWidth - frenchArcLineWidth / 2;

      // Swing arcs.
      for (const leaf of leaves) {
        ctx.beginPath();
        ctx.arc(
          leaf.hingeX,
          leaf.hingeZ,
          radiusOf(leaf),
          leaf.closedAngle,
          leaf.openAngle,
          leaf.swingAngle < 0 // anticlockwise when sweeping negative
        );
        ctx.strokeStyle = strokeStyle;
        ctx.lineWidth = frenchArcLineWidth;
        ctx.setLineDash([Math.max(0.05, 5 / scale), Math.max(0.05, 5 / scale)]);
        ctx.stroke();
      }

      // Each leaf in its fully open position. Both endpoints are offset
      // along the wall (toward the centre) by the same amount so the line
      // stays parallel to the true open direction while the butt cap at
      // the hinge doesn't poke past the frame rectangle.
      for (const leaf of leaves) {
        const radius = radiusOf(leaf);
        const offX = Math.cos(leaf.closedAngle) * (openLineWidth / 2);
        const offZ = Math.sin(leaf.closedAngle) * (openLineWidth / 2);
        ctx.beginPath();
        ctx.moveTo(leaf.hingeX + offX, leaf.hingeZ + offZ);
        ctx.lineTo(
          leaf.hingeX + Math.cos(leaf.openAngle) * radius + offX,
          leaf.hingeZ + Math.sin(leaf.openAngle) * radius + offZ
        );
        ctx.setLineDash([]);
        ctx.strokeStyle = strokeStyle;
        ctx.lineWidth = openLineWidth;
        ctx.stroke();
      }

      // Centre meeting line (where the leaves meet when closed).
      ctx.beginPath();
      ctx.moveTo(centerX - perpDir[0] * 0.1, centerZ - perpDir[1] * 0.1);
      ctx.lineTo(centerX + perpDir[0] * 0.1, centerZ + perpDir[1] * 0.1);
      ctx.strokeStyle = strokeStyle;
      ctx.lineWidth = Math.max(0.01, 1 / scale);
      ctx.setLineDash([]);
      ctx.stroke();
    } else {
      // Swing door - use explicit config from doorType
      const { hingeAtStart, swingAngle } = getSwingDoorConfig(
        doorType,
        door.wallFacesPriorityRoom
      );

      // Determine hinge position based on doorType config
      const hingeX = hingeAtStart ? startX : endX;
      const hingeZ = hingeAtStart ? startZ : endZ;
      const oppositeX = hingeAtStart ? endX : startX;
      const oppositeZ = hingeAtStart ? endZ : startZ;

      // Calculate door swing arc
      // Inset radius by half the stroke width so the outer edge of the
      // arc aligns with the door opening width (frame rectangle edges).
      const arcLineWidth = isSelected
        ? HIGHLIGHT_COLORS.MEDIUM_LINE(scale)
        : HIGHLIGHT_COLORS.THIN_LINE(scale);
      const swingRadius = door.dimensions.x - arcLineWidth / 2;

      // Calculate the angle of the door in closed position
      const closedAngle = Math.atan2(oppositeZ - hingeZ, oppositeX - hingeX);

      // Calculate swing arc angles
      const startAngle = closedAngle;
      const endAngle = closedAngle + swingAngle;

      // Draw swing arc
      ctx.beginPath();
      ctx.arc(
        hingeX,
        hingeZ,
        swingRadius,
        startAngle,
        endAngle,
        swingAngle < 0 // anticlockwise for outswing (negative angle)
      );
      ctx.lineWidth = arcLineWidth;
      ctx.strokeStyle = isSelected ? HIGHLIGHT_COLORS.PRIMARY : '#0066cc';
      ctx.setLineDash([Math.max(0.05, 5 / scale), Math.max(0.05, 5 / scale)]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw door in open position (line from hinge to end of arc)
      const openAngle = closedAngle + swingAngle;
      const openLineWidth = isSelected
        ? HIGHLIGHT_COLORS.THICK_LINE(scale)
        : Math.max(0.02, 2 / scale);
      // Offset both endpoints along the wall (toward opposite end) by the same
      // amount so the line stays exactly parallel to the true open direction
      // while the butt cap at the hinge doesn't poke past the frame rectangle.
      const wallOffset = openLineWidth / 2;
      const openStartX = hingeX + Math.cos(closedAngle) * wallOffset;
      const openStartZ = hingeZ + Math.sin(closedAngle) * wallOffset;
      const openDoorEndX =
        hingeX +
        Math.cos(openAngle) * swingRadius +
        Math.cos(closedAngle) * wallOffset;
      const openDoorEndZ =
        hingeZ +
        Math.sin(openAngle) * swingRadius +
        Math.sin(closedAngle) * wallOffset;

      ctx.beginPath();
      ctx.moveTo(openStartX, openStartZ);
      ctx.lineTo(openDoorEndX, openDoorEndZ);
      ctx.lineWidth = openLineWidth;
      ctx.strokeStyle = isSelected ? HIGHLIGHT_COLORS.PRIMARY : '#0066cc';
      ctx.stroke();
    }

    // In bold mode, draw frame rectangles at each end of the door opening
    if (showRoomWallsBold) {
      // Get the structure wall thickness from the parent wall's alignment
      let boldDoorThickness = 0.1143; // Default 4.5 inches

      if (door.parentWallIds?.length > 0) {
        const parentWallId = door.parentWallIds[0];
        // Direct lookup: parentWallIds may already reference a structure wall
        const structureWall = structureWalls.find(
          (sw) => sw.id === parentWallId
        );
        if (structureWall?.dimensions?.z) {
          boldDoorThickness = structureWall.dimensions.z;
        } else {
          // Indirect lookup: room wall → alignment → structure wall
          const parentRoomWall = walls.find((w) => w.id === parentWallId);
          if (parentRoomWall?.alignment?.structureWallId) {
            const sw = structureWalls.find(
              (s) => s.id === parentRoomWall.alignment.structureWallId
            );
            if (sw?.dimensions?.z) {
              boldDoorThickness = sw.dimensions.z;
            }
          }
        }
      }

      const frameWidth = 0.06; // 6cm frame width along door direction
      const frameThickness = boldDoorThickness;
      const frameColor = isSelected ? HIGHLIGHT_COLORS.PRIMARY : '#0066cc';

      ctx.fillStyle = frameColor;

      // Frame at start end
      const startFrameCorners = [
        [
          startX - perpDir[0] * (frameThickness / 2),
          startZ - perpDir[1] * (frameThickness / 2),
        ],
        [
          startX + perpDir[0] * (frameThickness / 2),
          startZ + perpDir[1] * (frameThickness / 2),
        ],
        [
          startX +
            normalizedDir[0] * frameWidth +
            perpDir[0] * (frameThickness / 2),
          startZ +
            normalizedDir[1] * frameWidth +
            perpDir[1] * (frameThickness / 2),
        ],
        [
          startX +
            normalizedDir[0] * frameWidth -
            perpDir[0] * (frameThickness / 2),
          startZ +
            normalizedDir[1] * frameWidth -
            perpDir[1] * (frameThickness / 2),
        ],
      ];

      ctx.beginPath();
      ctx.moveTo(startFrameCorners[0][0], startFrameCorners[0][1]);
      for (let i = 1; i < startFrameCorners.length; i++) {
        ctx.lineTo(startFrameCorners[i][0], startFrameCorners[i][1]);
      }
      ctx.closePath();
      ctx.fill();

      // Frame at end
      const endFrameCorners = [
        [
          endX - perpDir[0] * (frameThickness / 2),
          endZ - perpDir[1] * (frameThickness / 2),
        ],
        [
          endX + perpDir[0] * (frameThickness / 2),
          endZ + perpDir[1] * (frameThickness / 2),
        ],
        [
          endX -
            normalizedDir[0] * frameWidth +
            perpDir[0] * (frameThickness / 2),
          endZ -
            normalizedDir[1] * frameWidth +
            perpDir[1] * (frameThickness / 2),
        ],
        [
          endX -
            normalizedDir[0] * frameWidth -
            perpDir[0] * (frameThickness / 2),
          endZ -
            normalizedDir[1] * frameWidth -
            perpDir[1] * (frameThickness / 2),
        ],
      ];

      ctx.beginPath();
      ctx.moveTo(endFrameCorners[0][0], endFrameCorners[0][1]);
      for (let i = 1; i < endFrameCorners.length; i++) {
        ctx.lineTo(endFrameCorners[i][0], endFrameCorners[i][1]);
      }
      ctx.closePath();
      ctx.fill();
    }

    // Draw door property label (only in debug mode)
    if (debugMode) {
      // Door type abbreviation labels for debug mode
      const typeLabels = {
        // Swing doors
        leftInswing: 'L-I',
        rightInswing: 'R-I',
        leftOutswing: 'L-O',
        rightOutswing: 'R-O',
        // French doors
        leftActiveInswingFrench: 'LA-IF',
        rightActiveInswingFrench: 'RA-IF',
        leftActiveOutswingFrench: 'LA-OF',
        rightActiveOutswingFrench: 'RA-OF',
        // Sliding doors
        leftHandleSliding: 'LH-S',
        rightHandleSliding: 'RH-S',
        // Pocket doors
        leftHandlePocket: 'LH-P',
        rightHandlePocket: 'RH-P',
      };

      let doorLabel = typeLabels[doorType] || '?';

      // Add sliding panel count for sliding doors
      if (
        isSlidingDoor(doorType) &&
        door.category?.door?.slidingPanelCount > 1
      ) {
        doorLabel += `-${door.category.door.slidingPanelCount}`;
      }

      // Position label at door center
      ctx.save();
      ctx.translate(door.center.x, door.center.z);
      ctx.scale(1 / scale, 1 / scale);

      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Measure text for background
      const textWidth = ctx.measureText(doorLabel).width;
      const padding = 3;

      // Draw background
      ctx.fillStyle = 'rgba(139, 69, 19, 0.9)'; // Brown background
      ctx.fillRect(-textWidth / 2 - padding, -8, textWidth + padding * 2, 16);

      // Draw text
      ctx.fillStyle = '#FFFFFF';
      ctx.fillText(doorLabel, 0, 0);

      ctx.restore();
    }

    if (dim) ctx.restore();
  });
};

/**
 * Draws openings
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Array} openings - Array of opening objects
 * @param {number} scale - Current zoom scale
 * @param {Object} selectedItem - Currently selected item
 * @deprecated Openings are now drawn as gaps in walls with light grey fill by drawWalls()
 */
export const drawOpenings = (
  ctx,
  openings,
  scale,
  selectedItem,
  debugMode = false,
  walls = [],
  showRoomWallsBold = false,
  structureWalls = [],
  showDemoedWalls = true,
  // Two-pass split for hidden demoed walls: the white "clear" must paint
  // BEFORE room objects (so objects sit on top), but the black end caps must
  // paint AFTER objects (so they stay visible). clearDemoedOnly=true is the
  // pre-objects pass (clear hidden demoed spans only); the default pass draws
  // normal opening frames + the demoed end caps.
  clearDemoedOnly = false
) => {
  // In bold mode, draw frame rectangles at each end of each opening
  // (The grey fill is still drawn by drawWalls as gaps)
  if (!showRoomWallsBold) return;

  openings.forEach((opening) => {
    if (!opening.center || !opening.dimensions) return;

    const isSelected = matchesOpeningSelection(opening, selectedItem);
    const frameWidth = 0.06; // 6cm frame width along opening direction
    const frameColor = isSelected ? HIGHLIGHT_COLORS.PRIMARY : '#0066cc';

    // Get the structure wall thickness from the parent wall's alignment
    let boldOpeningThickness = 0.1143; // Default 4.5 inches

    if (opening.parentWallIds?.length > 0) {
      const parentWallId = opening.parentWallIds[0];
      // Direct lookup: parentWallIds may already reference a structure wall
      const structureWall = structureWalls.find((sw) => sw.id === parentWallId);
      if (structureWall?.dimensions?.z) {
        boldOpeningThickness = structureWall.dimensions.z;
      } else {
        // Indirect lookup: room wall → alignment → structure wall
        const parentRoomWall = walls.find((w) => w.id === parentWallId);
        if (parentRoomWall?.alignment?.structureWallId) {
          const sw = structureWalls.find(
            (s) => s.id === parentRoomWall.alignment.structureWallId
          );
          if (sw?.dimensions?.z) {
            boldOpeningThickness = sw.dimensions.z;
          }
        }
      }
    }

    // Calculate opening direction using rotation (same as doors/windows)
    const normalizedDir = apply3DRotationTo2D(opening.rotation);
    const perpDir = [-normalizedDir[1], normalizedDir[0]];

    // Calculate opening endpoints
    const halfWidth = opening.dimensions.x / 2;
    const startX = opening.center.x - normalizedDir[0] * halfWidth;
    const startZ = opening.center.z - normalizedDir[1] * halfWidth;
    const endX = opening.center.x + normalizedDir[0] * halfWidth;
    const endZ = opening.center.z + normalizedDir[1] * halfWidth;

    const halfThickness = boldOpeningThickness / 2;

    // Hidden demoed walls render in two passes (see clearDemoedOnly above).
    // Widened: new-field demoed openings also hide their span when toggle is OFF.
    const isHiddenDemoed =
      (isDemoedOpening(opening) || isDemoed(opening)) && !showDemoedWalls;
    if (clearDemoedOnly) {
      // Pre-objects pass: only clear hidden demoed spans; skip everything else.
      if (!isHiddenDemoed) return;
      // Both the structure pre-pass (grey #A9A9A9 outline) and the main wall
      // loop (black room-wall outline) have stroked wall faces across this
      // gap. White-fill the opening footprint — expanded perpendicular by the
      // face-stroke width so it also covers those edge strokes — leaving true
      // clear space. Done before objects so room objects draw on top.
      const pad = Math.max(0.02, 2 / scale);
      const ht = halfThickness + pad;
      const footprint = [
        [startX - perpDir[0] * ht, startZ - perpDir[1] * ht],
        [endX - perpDir[0] * ht, endZ - perpDir[1] * ht],
        [endX + perpDir[0] * ht, endZ + perpDir[1] * ht],
        [startX + perpDir[0] * ht, startZ + perpDir[1] * ht],
      ];
      ctx.save();
      ctx.fillStyle = 'white';
      ctx.beginPath();
      ctx.moveTo(footprint[0][0], footprint[0][1]);
      for (let i = 1; i < footprint.length; i++) {
        ctx.lineTo(footprint[i][0], footprint[i][1]);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      return;
    }
    if (isHiddenDemoed) {
      // Post-objects pass: the span is already cleared (clearDemoedOnly pass);
      // stroke thin black end-cap lines (room-wall convention) across the wall
      // thickness so the opening reads as a plain wall break. Pink if selected.
      ctx.save();
      ctx.strokeStyle = isSelected
        ? HIGHLIGHT_COLORS.PRIMARY
        : ELEMENT_COLORS.WALL_BLACK;
      ctx.lineWidth = Math.max(0.02, 2 / scale);
      ctx.beginPath();
      ctx.moveTo(
        startX - perpDir[0] * halfThickness,
        startZ - perpDir[1] * halfThickness
      );
      ctx.lineTo(
        startX + perpDir[0] * halfThickness,
        startZ + perpDir[1] * halfThickness
      );
      ctx.moveTo(
        endX - perpDir[0] * halfThickness,
        endZ - perpDir[1] * halfThickness
      );
      ctx.lineTo(
        endX + perpDir[0] * halfThickness,
        endZ + perpDir[1] * halfThickness
      );
      ctx.stroke();
      ctx.restore();
      return;
    }

    // When selected, fill the entire opening footprint with the pink
    // highlight first — the per-edge frame rectangles drawn after
    // strengthen the start/end markers, but the whole-length fill
    // makes the selection unambiguous (previously only the two end
    // frames lit up, which read as "two end caps highlighted").
    if (isSelected) {
      const fullFootprint = [
        [
          startX - perpDir[0] * halfThickness,
          startZ - perpDir[1] * halfThickness,
        ],
        [endX - perpDir[0] * halfThickness, endZ - perpDir[1] * halfThickness],
        [endX + perpDir[0] * halfThickness, endZ + perpDir[1] * halfThickness],
        [
          startX + perpDir[0] * halfThickness,
          startZ + perpDir[1] * halfThickness,
        ],
      ];
      ctx.save();
      ctx.fillStyle = HIGHLIGHT_COLORS.PRIMARY_FILL;
      ctx.strokeStyle = HIGHLIGHT_COLORS.PRIMARY;
      ctx.lineWidth = HIGHLIGHT_COLORS.THICK_LINE(scale);
      ctx.beginPath();
      ctx.moveTo(fullFootprint[0][0], fullFootprint[0][1]);
      for (let i = 1; i < fullFootprint.length; i++) {
        ctx.lineTo(fullFootprint[i][0], fullFootprint[i][1]);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }

    ctx.fillStyle = frameColor;

    // Frame at start edge
    const startFrameCorners = [
      [
        startX - perpDir[0] * halfThickness,
        startZ - perpDir[1] * halfThickness,
      ],
      [
        startX + perpDir[0] * halfThickness,
        startZ + perpDir[1] * halfThickness,
      ],
      [
        startX + normalizedDir[0] * frameWidth + perpDir[0] * halfThickness,
        startZ + normalizedDir[1] * frameWidth + perpDir[1] * halfThickness,
      ],
      [
        startX + normalizedDir[0] * frameWidth - perpDir[0] * halfThickness,
        startZ + normalizedDir[1] * frameWidth - perpDir[1] * halfThickness,
      ],
    ];

    ctx.beginPath();
    ctx.moveTo(startFrameCorners[0][0], startFrameCorners[0][1]);
    for (let i = 1; i < startFrameCorners.length; i++) {
      ctx.lineTo(startFrameCorners[i][0], startFrameCorners[i][1]);
    }
    ctx.closePath();
    ctx.fill();

    // Frame at end edge
    const endFrameCorners = [
      [endX - perpDir[0] * halfThickness, endZ - perpDir[1] * halfThickness],
      [endX + perpDir[0] * halfThickness, endZ + perpDir[1] * halfThickness],
      [
        endX - normalizedDir[0] * frameWidth + perpDir[0] * halfThickness,
        endZ - normalizedDir[1] * frameWidth + perpDir[1] * halfThickness,
      ],
      [
        endX - normalizedDir[0] * frameWidth - perpDir[0] * halfThickness,
        endZ - normalizedDir[1] * frameWidth - perpDir[1] * halfThickness,
      ],
    ];

    ctx.beginPath();
    ctx.moveTo(endFrameCorners[0][0], endFrameCorners[0][1]);
    for (let i = 1; i < endFrameCorners.length; i++) {
      ctx.lineTo(endFrameCorners[i][0], endFrameCorners[i][1]);
    }
    ctx.closePath();
    ctx.fill();

    // Overlay red hatch for a visible new-field demoed opening (NOT a marker).
    // Only runs in main pass (clearDemoedOnly=false) when toggle is ON — the
    // early-return above already skipped the toggle-OFF case.
    if (showDemoedWalls && isDemoed(opening) && !isDemoedOpening(opening)) {
      const hatchCorners = [
        [
          startX - perpDir[0] * halfThickness,
          startZ - perpDir[1] * halfThickness,
        ],
        [endX - perpDir[0] * halfThickness, endZ - perpDir[1] * halfThickness],
        [endX + perpDir[0] * halfThickness, endZ + perpDir[1] * halfThickness],
        [
          startX + perpDir[0] * halfThickness,
          startZ + perpDir[1] * halfThickness,
        ],
      ];
      drawDemoedWallHatch(ctx, hatchCorners, scale, isSelected);
    }
  });
};

/**
 * Draws objects on the floor plan
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Array} objectsArray - Array of objects to draw
 * @param {Object} data - Full structure data
 * @param {number} scale - Current zoom scale
 * @param {Object} selectedItem - Currently selected item
 */
/**
 * Draw bathroom fixtures on the floor plan
 */
export const drawBathroomFixtures = (ctx, fixtures, scale, selectedItem) => {
  if (!fixtures || fixtures.length === 0) return;

  const fixtureColors = {
    toilet: '#8B4513', // Saddle brown
    sink: '#4682B4', // Steel blue
    shower: '#87CEEB', // Sky blue
    bathtub: '#FFD700', // Gold
  };

  const fixtureIcons = {
    toilet: '🚽',
    sink: '🚰',
    shower: '🚿',
    bathtub: '🛁',
  };

  fixtures.forEach((fixture) => {
    const fixtureType = fixture.fixtureType || fixture.type;
    const color = fixtureColors[fixtureType] || '#808080';
    const icon = fixtureIcons[fixtureType] || '🔧';

    // Get fixture position
    const x = fixture.center?.x || fixture.x || 0;
    const z = fixture.center?.z || fixture.y || 0;

    // Get fixture dimensions
    const width = fixture.dimensions?.x || fixture.width || 0.5;
    const depth = fixture.dimensions?.z || fixture.depth || 0.5;

    // Draw fixture rectangle
    ctx.save();

    // Draw fixture body
    ctx.fillStyle = color + '40'; // Semi-transparent fill
    ctx.strokeStyle = color;
    ctx.lineWidth = 2 / scale;

    // Draw based on corners if available, otherwise use center and dimensions
    if (fixture.physicalCorners && fixture.physicalCorners.length > 0) {
      ctx.beginPath();
      fixture.physicalCorners.forEach((corner, i) => {
        if (i === 0) {
          ctx.moveTo(corner.x, corner.z || corner.y);
        } else {
          ctx.lineTo(corner.x, corner.z || corner.y);
        }
      });
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    } else {
      // Draw rectangle from center
      ctx.fillRect(x - width / 2, z - depth / 2, width, depth);
      ctx.strokeRect(x - width / 2, z - depth / 2, width, depth);
    }

    // Draw fixture icon/label with reasonable size
    ctx.fillStyle = '#333';
    // Use a fixed size that's readable but not huge (20-40px depending on scale)
    const fontSize = Math.min(40, Math.max(20, 30 / Math.sqrt(scale)));
    ctx.font = `${fontSize}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(icon, x, z);

    // Highlight if selected
    if (
      selectedItem?.type === 'bathroomFixture' &&
      selectedItem?.element?.id === fixture.id
    ) {
      ctx.strokeStyle = '#ff6b6b';
      ctx.lineWidth = 4 / scale;
      ctx.setLineDash([5 / scale, 5 / scale]);
      ctx.strokeRect(
        x - width / 2 - 0.1,
        z - depth / 2 - 0.1,
        width + 0.2,
        depth + 0.2
      );
      ctx.setLineDash([]);
    }

    ctx.restore();
  });
};

/**
 * Helper function to draw a sink on a countertop
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} sink - Sink object
 * @param {Object} countertop - Parent countertop object
 * @param {number} scale - Scale factor
 * @param {boolean} isSelected - Whether sink is selected
 */
const drawSinkOnCountertop = (
  ctx,
  sink,
  countertop,
  scale,
  isSelected,
  highlightColor = null
) => {
  // Get sink position - handle both 'center' (new) and 'position' (legacy) fields
  const sinkPos = sink.center || sink.position;
  if (!sinkPos) return;

  // Kitchen sinks dispatch to drawObjectShape via the top-level type
  // (rectangular rounded basin). Bathroom sinks dispatch via their
  // mount sinkType ('undermount' / 'vessel' / 'dropin' → oval).
  const sinkType = isKitchenSink(sink)
    ? 'kitchen-sink'
    : getSinkType(sink) || 'sink';
  const sinkDimensions = sink.dimensions || {
    width: 0.559,
    depth: 0.457,
  };

  // Use the sink's own rotation (same derivation drawObject uses for every object).
  // The sink is placed in world coordinates with its own rotation; the countertop's
  // rotation doesn't matter here because sink.center is already in world space.
  const normalizedDir = apply3DRotationTo2D(sink.rotation, [0, 0, 1]);
  const objectAngle = Math.atan2(normalizedDir[1], normalizedDir[0]);

  ctx.save();
  ctx.translate(sinkPos.x, sinkPos.z);
  ctx.rotate(objectAngle);

  // Set sink color - when selected, derive fill from highlightColor (40%
  // opacity hex suffix) so SECONDARY blue tints with a light-blue fill
  // instead of the PRIMARY pink. Mirrors drawObject's fill convention.
  if (isSelected) {
    ctx.fillStyle = highlightColor
      ? highlightColor + '66'
      : HIGHLIGHT_COLORS.PRIMARY_FILL;
    ctx.strokeStyle = highlightColor || HIGHLIGHT_COLORS.PRIMARY;
    ctx.lineWidth = HIGHLIGHT_COLORS.THICK_LINE(scale);
  } else {
    ctx.fillStyle = ELEMENT_COLORS.OBJECT_BATHROOM;
    ctx.strokeStyle = ELEMENT_COLORS.OBJECT_STROKE;
    ctx.lineWidth = 2 / scale;
  }

  // Draw the sink shape
  drawObjectShape(
    ctx,
    sinkType,
    sinkDimensions.width,
    sinkDimensions.cutoutDepth || 0.2,
    sinkDimensions.depth,
    scale,
    0, // No rotation
    sink.attributes || {},
    sink // a double-bowl kitchen sink draws two bowls
  );

  ctx.restore();
};

export const drawObjects = (
  ctx,
  objectsArray,
  data,
  scale,
  selectedItem,
  debugMode = false,
  {
    highlightColor = null, // Optional override for selection highlight color
    highlightObjectIds = null, // Set<string> — render with PRIMARY highlight
    secondaryHighlightObjectIds = null, // Set<string> — render with SECONDARY highlight
    inFocusObjectIds = null, // Set<string> — when set, non-focus objects render at 0.5 alpha
    showDemoedWalls = true,
    electricalMode = false, // Electrical drawing: lights render as plan symbols
    // Called with each object just before it is drawn, in draw order. The
    // inspection report draws its plans with this function through an SVG
    // stand-in canvas and needs to know which object each shape belongs to
    // (features/inspection-report/planDrawing.js).
    onObjectStart = null,
  } = {}
) => {
  if (!objectsArray) return;

  // Use the helper function to filter objects by height
  const filteredObjects = filterObjectsByHeight(objectsArray, data);

  // Sort objects to draw bathroom fixtures last (on top) in non-debug mode
  const sortedObjects = debugMode
    ? filteredObjects
    : (() => {
        // Total-ordering by layer index — earlier callers tried a
        // pairwise comparator with rules like "countertop < dishwasher"
        // alongside "keep original order" for everything else, but
        // those constraints conflicted (cabinets emitted before
        // countertop, dishwasher emitted before cabinets) and V8's
        // sort produced undefined orderings where appliances ended up
        // drawn under cabinets. A flat layer index removes the
        // ambiguity.
        const getLayer = (obj, type) => {
          // 5: high objects (uppers, fridge surround, etc.) drawn last so
          //    their translucent fill + dashed outline overlay the
          //    counter-height stack below — without this an upper
          //    cabinet sitting directly above a lower one would be
          //    completely hidden in plan view.
          if (obj.isHighObject) return 5;
          // 0: cabinets — bottom of the stack
          if (
            type === 'lower-cabinet' ||
            type === 'upper-cabinet' ||
            type === 'blank-panel' ||
            obj.category?.['lower-cabinet'] ||
            obj.category?.['upper-cabinet'] ||
            obj.category?.['blank-panel']
          ) {
            return 0;
          }
          // 1: countertops on top of cabinets (counter hides cab outline)
          if (type === 'countertop' || obj.category?.countertop) {
            return 1;
          }
          // 1.5: L-shaped corner cabinets above countertops so the L's
          //      missing wedge isn't hidden under the rectangular run
          //      countertops that span the full corner box. Single-
          //      sided corner cabinets are rectangular and stay at
          //      layer 0 alongside lower cabinets.
          if (
            (type === 'corner-cabinet' || obj.category?.['corner-cabinet']) &&
            obj.dimensions?.cornerVariant !== 'single-sided'
          ) {
            return 1.5;
          }
          if (type === 'corner-cabinet' || obj.category?.['corner-cabinet']) {
            return 0;
          }
          // 2: kitchen appliances on top of the countertop so they
          //    remain visible (dishwasher, range/stove, fridge,
          //    wall-oven/oven). Within this layer, ovens before ranges
          //    so range burners draw on top. Both old (stove / oven) and
          //    new (range / wall-oven) types accepted.
          if (
            type === 'oven' ||
            type === 'wall-oven' ||
            obj.category?.oven ||
            obj.category?.['wall-oven']
          ) {
            return 2.0;
          }
          if (
            type === 'stove' ||
            type === 'range' ||
            type === 'cooktop' ||
            obj.category?.stove ||
            obj.category?.range ||
            obj.category?.cooktop
          ) {
            return 2.1;
          }
          if (
            type === 'dishwasher' ||
            type === 'refrigerator' ||
            obj.category?.dishwasher ||
            obj.category?.refrigerator
          ) {
            return 2.2;
          }
          // 4: faucets last (drawn over sinks)
          if (
            type === 'bathroom-sink-faucet' ||
            type === 'faucet' ||
            type === 'kitchen-faucet'
          ) {
            return 4;
          }
          // 3: bathroom fixtures + everything else above appliances
          if (isBathroomFixtureType(type)) return 3;
          return 2.5;
        };
        return [...filteredObjects].sort((a, b) => {
          const typeA = getObjectType(a).toLowerCase();
          const typeB = getObjectType(b).toLowerCase();
          const la = getLayer(a, typeA);
          const lb = getLayer(b, typeB);
          if (la !== lb) return la - lb;
          return 0; // same layer — keep original (stable sort)
        });
      })();

  // (Removed: the selected object used to get hoisted to the end of
  // sortedObjects so it would draw on top. We rely on opacity dimming
  // instead — hoisting the selected one breaks composite fixtures like
  // tub-support-box + bathtub, where the surround would draw on top of
  // the inset bathtub once selected and hide it entirely.)

  // Identify corner tub fixture groups — their tub-support-box / tub-surface
  // children are redundant in plan view (the bathtub's polygon drawing already
  // covers the full surround) and would otherwise draw a rectangular collision
  // indicator that extends beyond the pentagon.
  const cornerTubGroupIds = new Set();
  for (const obj of sortedObjects) {
    if (
      (getObjectType(obj) || '').toLowerCase() === 'bathtub' &&
      obj.usePolygonSurround === true &&
      obj.fixtureGroupId
    ) {
      cornerTubGroupIds.add(obj.fixtureGroupId);
    }
  }

  // NOTE: the rest of a selected cabinet run is NOT highlighted from here.
  // This file used to derive its own `runPeerIds` from `selectedItem.runId` and
  // paint them SECONDARY, which (a) duplicated the caller's own set and (b) was
  // blind to the membership rule — a fridge surround carries no `runId`. Which
  // run members light, and in which tier, is the CALLER's answer now: it depends
  // on which cabinet panel is open (`hooks/useCabinetEditScope`), and the only
  // caller that can know that passes them in `highlightObjectIds`
  // (`2d/useFloorplanRenderer.js` → `objectPrimaryHighlight`).

  // Draw objects in two passes:
  // Pass 1: Draw all non-faucet objects (including sinks on countertops)
  sortedObjects.forEach((obj, index) => {
    const objectType = getObjectType(obj);
    const isFaucet =
      objectType.toLowerCase() === 'bathroom-sink-faucet' ||
      objectType.toLowerCase() === 'faucet';

    // Skip faucets in first pass
    if (isFaucet) return;

    // Skip sinks with parent countertops — they're drawn by the
    // dedicated drawSinkOnCountertop pass below
    if (isSink(obj) && obj.parents && obj.parents.length > 0) return;

    // Skip tub child objects for corner tubs — the bathtub's polygon
    // drawing already covers the full surround
    const objLower = objectType.toLowerCase();
    if (
      (objLower === 'tub-support-box' || objLower === 'tub-surface') &&
      obj.fixtureGroupId &&
      cornerTubGroupIds.has(obj.fixtureGroupId)
    ) {
      return;
    }

    // Demo-field gate: hide demoed objects when toggle is OFF
    if (isDemoed(obj)) {
      if (!showDemoedWalls) return; // demo toggle OFF → after-demo look (hide)
    }

    const isReallySelected = selectedItem && selectedItem.id === obj.id;
    const isMeasurementHighlighted =
      !isReallySelected &&
      highlightObjectIds &&
      obj.id &&
      highlightObjectIds.has(obj.id);
    const isSecondaryHighlighted =
      !isReallySelected &&
      !isMeasurementHighlighted &&
      obj.id &&
      secondaryHighlightObjectIds &&
      secondaryHighlightObjectIds.has(obj.id);
    // Treat highlighted objects visually like the selected one so the
    // canvas highlights them in sync with the dim line. Real selection
    // state (drag handles, panel) stays driven by selectedItem.
    const isSelected =
      isReallySelected || isMeasurementHighlighted || isSecondaryHighlighted;
    // Pick the highlight color tier for this object:
    //   really selected → caller-provided (e.g. green during move)
    //   primary highlight → leave as null so drawObject uses PRIMARY
    //   secondary highlight → SECONDARY (softer pink)
    const objHighlightColor = isReallySelected
      ? highlightColor
      : isSecondaryHighlighted
        ? HIGHLIGHT_COLORS.SECONDARY
        : null;

    if (objectType === 'chair' && isReallySelected) {
      console.log('Drawing selected chair:', {
        id: obj.id,
        center: obj.center,
        hasCollision: obj.hasCollision,
      });
    }

    // Dim non-focus objects to 0.5 alpha when something is in focus.
    const dim = inFocusObjectIds && obj.id && !inFocusObjectIds.has(obj.id);
    if (dim) {
      ctx.save();
      ctx.globalAlpha *= 0.5;
    }
    if (onObjectStart) onObjectStart(obj);
    drawObject(ctx, obj, {
      isSelected,
      objectType,
      scale,
      index,
      debugMode,
      selectedItem,
      highlightColor: isSelected ? objHighlightColor : null,
      demoHatch: isDemoed(obj) && showDemoedWalls,
      electricalMode,
    });
    if (dim) ctx.restore();
  });

  // Draw standalone sinks (new storage location in room.objects[] with parents array)
  // This supports the new parent-child relationship system
  sortedObjects.forEach((obj) => {
    if (isSink(obj) && obj.parents && obj.parents.length > 0) {
      // Demo-field gate (hide-only — no hatch for sinks on countertops)
      if (isDemoed(obj) && !showDemoedWalls) return;

      // This is a new-style sink with parent relationship
      // Find the parent countertop to draw the sink on
      const parentCountertop = findSinkParentCountertop(obj, {
        objects: objectsArray,
      });

      if (parentCountertop) {
        const isReallySelected = selectedItem && selectedItem.id === obj.id;
        // A sink is run cabinetry too — its base is a bay — so it takes the
        // PRIMARY tier when the caller names it, exactly as the main pass does.
        // It was the one object in the scene that could not: this pass knew only
        // about the secondary set, so a run-scope highlight stopped at the
        // countertop and left the sink in it unlit.
        const isPrimary =
          !isReallySelected &&
          obj.id &&
          highlightObjectIds &&
          highlightObjectIds.has(obj.id);
        const isSecondary =
          !isReallySelected &&
          !isPrimary &&
          obj.id &&
          secondaryHighlightObjectIds &&
          secondaryHighlightObjectIds.has(obj.id);
        const sinkHighlightColor = isReallySelected
          ? highlightColor
          : isSecondary
            ? HIGHLIGHT_COLORS.SECONDARY
            : null;
        const dim = inFocusObjectIds && obj.id && !inFocusObjectIds.has(obj.id);
        if (dim) {
          ctx.save();
          ctx.globalAlpha *= 0.5;
        }
        if (onObjectStart) onObjectStart(obj);
        drawSinkOnCountertop(
          ctx,
          obj,
          parentCountertop,
          scale,
          isReallySelected || isPrimary || isSecondary,
          sinkHighlightColor
        );
        if (dim) ctx.restore();
      }
    }
  });

  // Pass 2: Draw all faucets last (on top of everything including sinks)
  sortedObjects.forEach((obj, index) => {
    const objectType = getObjectType(obj);
    const isFaucet =
      objectType.toLowerCase() === 'bathroom-sink-faucet' ||
      objectType.toLowerCase() === 'faucet';

    // Only draw faucets in second pass
    if (!isFaucet) return;

    // Demo-field gate: hide demoed faucets when toggle is OFF
    if (isDemoed(obj)) {
      if (!showDemoedWalls) return; // demo toggle OFF → after-demo look (hide)
    }

    const isReallySelected = selectedItem && selectedItem.id === obj.id;
    const isSecondary =
      !isReallySelected &&
      secondaryHighlightObjectIds &&
      obj.id &&
      secondaryHighlightObjectIds.has(obj.id);
    const faucetHighlightColor = isReallySelected
      ? highlightColor
      : isSecondary
        ? HIGHLIGHT_COLORS.SECONDARY
        : null;
    const dim = inFocusObjectIds && obj.id && !inFocusObjectIds.has(obj.id);
    if (dim) {
      ctx.save();
      ctx.globalAlpha *= 0.5;
    }
    if (onObjectStart) onObjectStart(obj);
    drawObject(ctx, obj, {
      isSelected: isReallySelected || isSecondary,
      objectType,
      scale,
      index, // Add index for debugging
      debugMode,
      selectedItem, // Pass selectedItem for sink selection checking
      highlightColor: faucetHighlightColor,
      demoHatch: isDemoed(obj) && showDemoedWalls,
      electricalMode,
    });
    if (dim) ctx.restore();
  });
};

/**
 * Draws a single object
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} obj - Object to draw
 * @param {Object} options - Drawing options
 */
export const drawObject = (ctx, obj, options = {}) => {
  const {
    isSelected = false,
    objectType = 'Unknown',
    scale = 1,
    index = 0,
    debugMode = false,
    selectedItem = null,
    highlightColor = null, // Optional override for selection stroke color
    demoHatch = false,
    // Optional override for the type-derived fill. The isolation views draw
    // model-less members beside transparent GLB edge-projections, where a
    // solid type colour reads as a slab next to line art; they pass white so
    // the shapes keep their outlines and internal detail without the mass.
    fillStyle = null,
    // Electrical drawing: lights render as standard plan symbols instead of
    // the decorative fixture shapes, and opt out of the high-object styling.
    electricalMode = false,
  } = options;

  // Debug resizing

  if (
    !obj.center ||
    obj.center.x === undefined ||
    obj.center.z === undefined ||
    !obj.dimensions
  ) {
    return;
  }

  const width = obj.dimensions.x;
  const height = obj.dimensions.y;
  const depth = obj.dimensions.z;

  // Check for zero or invalid dimensions
  if (width <= 0 || height <= 0 || depth <= 0) {
    return;
  }

  // Use object's center position (x, z for 2D floor plan)
  const x = obj.center.x;
  const z = obj.center.z;

  // Apply full 3D rotation to get object's 2D orientation
  // All objects now use the same rotation handling
  const normalizedDir = apply3DRotationTo2D(obj.rotation, [0, 0, 1]);
  const objectAngle = Math.atan2(normalizedDir[1], normalizedDir[0]);

  // Debug: Log rotation for wall-lights and toilets
  // if (objectType === 'Wall-light' || objectType === 'Toilet') {
  //   console.log(`DRAWING ${objectType}:`, {
  //     rotation: obj.rotation,
  //     normalizedDir,
  //     objectAngle: (objectAngle * 180) / Math.PI,
  //     objectAngleNegated:
  //       (Math.atan2(-normalizedDir[1], normalizedDir[0]) * 180) / Math.PI,
  //   });
  // }

  ctx.save();

  // Move to object position
  ctx.translate(x, z);

  // For diagonal corner showers, skip rotation - geometry is built aligned to walls
  // (matching 3D viewer which sets rotation to (0,0,0) for these)
  // Only diagonal door showers use wall-angle-based geometry
  const isDiagonalCornerShower =
    objectType.toLowerCase() === 'shower' &&
    obj.wallCount >= 2 &&
    obj.doorType === 'diagonal' &&
    obj.attachedWallAngles &&
    obj.attachedWallAngles.length === 2;

  const objTypeLower = objectType.toLowerCase();
  const isPolygonSurroundTub =
    (objTypeLower === 'bathtub' || objTypeLower === 'tub-support-box') &&
    obj.usePolygonSurround === true;

  if (!isDiagonalCornerShower && !isPolygonSurroundTub) {
    // Apply the calculated rotation
    ctx.rotate(objectAngle);
  }

  // Check bathroom fixtures first (before debug mode) to ensure consistent colors
  const isBathroomFixture = isBathroomFixtureType(objectType);

  // A light drawn as an electrical symbol is styled by the symbol, not by the
  // plan: no ghosted high-object fill and no dashed "above the cut plane"
  // outline. That convention belongs to the architectural drawing — on an
  // electrical sheet every symbol is a solid line.
  const drawsLightSymbol = electricalMode && lightSymbolOf(obj) !== null;

  // Check if this is a high object (above 48 inches)
  const isHighObject = obj.isHighObject === true && !drawsLightSymbol;

  // Check if this is a structure-level object
  const isStructureLevel = obj._isStructureLevel === true;

  // Set object style
  if (isSelected) {
    const fillColor = highlightColor
      ? highlightColor + '66' // 40% opacity hex suffix
      : HIGHLIGHT_COLORS.PRIMARY_FILL;
    ctx.fillStyle = isHighObject
      ? 'rgba(59, 130, 246, 0.3)' // Lighter blue for high selected objects
      : fillColor;
  } else if (isStructureLevel) {
    // Purple shading for structure-level objects
    ctx.fillStyle = isHighObject
      ? 'rgba(139, 0, 255, 0.3)' // Lighter purple for high structure objects
      : 'rgba(139, 0, 255, 0.6)'; // Purple for structure-level objects
  } else if (isBathroomFixture) {
    // Bathroom fixtures always use grey (even in debug mode)
    ctx.fillStyle = isHighObject
      ? 'rgba(224, 224, 224, 0.5)' // Lighter grey for high bathroom fixtures
      : ELEMENT_COLORS.OBJECT_BATHROOM;
  } else if (isElectricalDeviceObject(obj)) {
    // Electrical devices draw their own symbol; no body fill.
    ctx.fillStyle = '#ffffff';
  } else if (
    (() => {
      const ot = (objectType || '').toLowerCase();
      // Both old (stove / oven) and new (range / wall-oven) names accepted.
      return (
        ot === 'dishwasher' ||
        ot === 'stove' ||
        ot === 'range' ||
        ot === 'cooktop' ||
        ot === 'refrigerator' ||
        ot === 'oven' ||
        ot === 'wall-oven' ||
        ot === 'kitchen-faucet' ||
        ot === 'countertop' ||
        obj.category?.dishwasher ||
        obj.category?.stove ||
        obj.category?.range ||
        obj.category?.cooktop ||
        obj.category?.refrigerator ||
        obj.category?.oven ||
        obj.category?.['wall-oven'] ||
        obj.category?.countertop
      );
    })()
  ) {
    // Kitchen surfaces and appliances use their designated colors so
    // they read as distinct rectangles in the floor plan. Without
    // this, every kitchen object falls through to OBJECT_NORMAL grey
    // and the appliance is indistinguishable from the countertop
    // beneath it. Countertop is included so it gets its lighter
    // `#F0F0F0`, contrasting with the darker appliance colors.
    ctx.fillStyle = isHighObject
      ? 'rgba(192, 192, 192, 0.3)'
      : getObjectColor(obj);
  } else if ((objectType || '').toLowerCase().startsWith('dorm-')) {
    ctx.fillStyle = isHighObject ? 'rgba(200, 210, 220, 0.4)' : '#D5DDE5';
  } else if (debugMode) {
    // Debug mode: use colorful palette for non-bathroom objects
    const debugColors = [
      '#FF6B6B',
      '#4ECDC4',
      '#45B7D1',
      '#96CEB4',
      '#FFEAA7',
      '#DDA0DD',
      '#98D8C8',
      '#F7DC6F',
    ];
    ctx.fillStyle = debugColors[index % debugColors.length];
  } else {
    // Non-debug mode: grey colors
    ctx.fillStyle = isHighObject
      ? 'rgba(156, 163, 175, 0.3)' // Lighter grey for high normal objects
      : ELEMENT_COLORS.OBJECT_NORMAL;
  }

  if (fillStyle) ctx.fillStyle = fillStyle;

  ctx.strokeStyle = isSelected
    ? highlightColor || HIGHLIGHT_COLORS.PRIMARY
    : ELEMENT_COLORS.OBJECT_STROKE;
  ctx.lineWidth = isSelected
    ? HIGHLIGHT_COLORS.THICK_LINE(scale)
    : HIGHLIGHT_COLORS.THIN_LINE(scale);

  // Collision indicator: red outline (no fill override — hatching is drawn after shape)
  if (obj.hasCollision) {
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = HIGHLIGHT_COLORS.THICK_LINE(scale);
  }

  // Use dashed line for high objects
  if (isHighObject) {
    ctx.setLineDash([5 / scale, 5 / scale]); // Dashed pattern scaled appropriately
  } else {
    ctx.setLineDash([]); // Solid line for normal objects
  }

  // Draw object shape based on type
  drawObjectShape(
    ctx,
    objectType,
    width,
    height,
    depth,
    scale,
    objectAngle,
    obj.attributes || {},
    obj, // Pass full object for accessing model metadata (e.g., faucet mounting points)
    { electricalMode }
  );

  // Draw diagonal red hatching over colliding objects
  if (obj.hasCollision) {
    ctx.save();
    const hd = depth / 2;
    const hw = width / 2;
    ctx.beginPath();
    if (isPolygonSurroundTub) {
      const polyData = obj['tub-surround-polygon'];
      const I2M = 0.0254;
      const polyAngle = -(obj.rotation?.y || 0);
      const cosA = Math.cos(polyAngle);
      const sinA = Math.sin(polyAngle);
      for (let i = 0; i < polyData.points.length; i++) {
        const px = polyData.points[i].x * I2M;
        const pz = polyData.points[i].z * I2M;
        const lx = px * cosA - pz * sinA;
        const lz = px * sinA + pz * cosA;
        if (i === 0) ctx.moveTo(lx, lz);
        else ctx.lineTo(lx, lz);
      }
      ctx.closePath();
    } else {
      ctx.rect(-hd, -hw, depth, width);
    }
    ctx.clip();
    ctx.strokeStyle = 'rgba(239, 68, 68, 0.5)';
    ctx.lineWidth = 1 / scale;
    ctx.setLineDash([]);
    const step = 6 / scale;
    const span = depth + width;
    for (let offset = -span; offset < span; offset += step) {
      ctx.beginPath();
      ctx.moveTo(-hd + offset, -hw);
      ctx.lineTo(-hd + offset + width, hw);
      ctx.stroke();
    }
    ctx.restore();
  }

  // Draw red diagonal hatch for demoed objects (mirrors collision-hatch geometry,
  // local space — we are already inside the translate+rotate transform)
  if (demoHatch) {
    ctx.save();
    const _hd = depth / 2;
    const _hw = width / 2;
    ctx.beginPath();
    ctx.rect(-_hd, -_hw, depth, width);
    ctx.clip();
    ctx.strokeStyle = isSelected ? '#CC0000' : '#990000';
    ctx.lineWidth = Math.max(0.003, 1.5 / scale);
    ctx.setLineDash([]);
    const _step = 0.04; // match drawDemoedWallHatch's 4cm world spacing
    const _span = depth + width;
    for (let _o = -_span; _o < _span; _o += _step) {
      ctx.beginPath();
      ctx.moveTo(-_hd + _o, -_hw);
      ctx.lineTo(-_hd + _o + width, _hw);
      ctx.stroke();
    }
    ctx.restore();
  }

  // Draw orientation indicator (only in debug mode)
  if (debugMode) {
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = Math.max(0.5 / scale, 1 / scale);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(width / 3, 0);
    ctx.stroke();

    // Draw arrowhead
    ctx.beginPath();
    ctx.moveTo(width / 3, 0);
    ctx.lineTo(width / 3 - 5 / scale, -3 / scale);
    ctx.moveTo(width / 3, 0);
    ctx.lineTo(width / 3 - 5 / scale, 3 / scale);
    ctx.stroke();
  }

  // Collision indicator is now handled by red fill/stroke color set earlier

  // Draw sinks on countertops
  if (
    (objectType.toLowerCase() === 'countertop' ||
      (obj.category && obj.category.countertop)) &&
    obj.sinks &&
    obj.sinks.length > 0
  ) {
    obj.sinks.forEach((sink) => {
      if (!sink.position) return;

      const sinkType = isKitchenSink(sink)
        ? 'kitchen-sink'
        : getSinkType(sink) || 'sink';
      const sinkDimensions = sink.dimensions || {
        width: 0.559,
        depth: 0.457,
      };

      // Check if this sink is selected (canonical type === 'sink')
      const isSinkSelected =
        selectedItem &&
        selectedItem.type &&
        selectedItem.type.includes('sink') &&
        selectedItem.position &&
        sink.position &&
        Math.abs(selectedItem.position.x - sink.position.x) < 0.01 &&
        Math.abs(selectedItem.position.z - sink.position.z) < 0.01;

      // Calculate sink position relative to countertop center
      // We're already translated to the countertop center (x, z) and rotated by objectAngle
      const worldRelativeX = sink.position.x - obj.center.x;
      const worldRelativeZ = sink.position.z - obj.center.z;

      // Transform world-relative coordinates into countertop's local rotated coordinate system
      // Rotate by -objectAngle to match the countertop's orientation
      const localX =
        Math.cos(-objectAngle) * worldRelativeX -
        Math.sin(-objectAngle) * worldRelativeZ;
      const localZ =
        Math.sin(-objectAngle) * worldRelativeX +
        Math.cos(-objectAngle) * worldRelativeZ;

      // Draw sink schematic at the sink position (in countertop's local coordinates)
      ctx.save();
      ctx.translate(localX, localZ);

      // Set sink color - pink if selected, grey otherwise (to match bathroom fixtures)
      if (isSinkSelected) {
        ctx.fillStyle = HIGHLIGHT_COLORS.PRIMARY_FILL;
        ctx.strokeStyle = HIGHLIGHT_COLORS.PRIMARY;
        ctx.lineWidth = HIGHLIGHT_COLORS.THICK_LINE(scale);
      } else {
        ctx.fillStyle = ELEMENT_COLORS.OBJECT_BATHROOM; // Grey like other bathroom fixtures
        ctx.strokeStyle = ELEMENT_COLORS.OBJECT_STROKE; // Dark grey stroke
        ctx.lineWidth = 2 / scale;
      }

      // Use drawObjectShape from objectDrawingUtils.js to render the sink schematic
      drawObjectShape(
        ctx,
        sinkType,
        sinkDimensions.width,
        sinkDimensions.cutoutDepth || 0.2,
        sinkDimensions.depth,
        scale,
        0 // No rotation
      );

      ctx.restore();
    });
  }

  ctx.restore();
};

/**
 * Draws coordinate axes in fixed screen position
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} canvasSize - Canvas dimensions {width, height}
 */
export const drawAxes = (ctx, canvasSize) => {
  const axisLength = 40; // pixels (fixed size)
  const arrowSize = 8; // pixels
  const margin = 80; // margin from edges

  // Position in bottom right corner with enough margin to be fully visible
  // Using window.innerHeight to ensure it's within the viewport
  const centerX = canvasSize.width - margin;
  const centerY = Math.min(canvasSize.height, window.innerHeight) - margin - 60; // Extra 60px to account for axes size

  // Screen space is the DPR transform, not identity — `canvasSize` is already
  // in CSS pixels (the caller divides), so resetting to identity drew the whole
  // widget at half size in the wrong corner on any HiDPI display.
  const dpr = enterScreenSpace(ctx);

  // === Draw angle direction indicator above the axes ===
  const angleIndicatorY = centerY - 80; // Position above axes
  ctx.translate(centerX, angleIndicatorY);

  // Background for angle indicator
  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.fillRect(-45, -35, 90, 60);
  ctx.strokeStyle = '#333333';
  ctx.lineWidth = 1;
  ctx.strokeRect(-45, -35, 90, 60);

  // Draw angle arc showing positive rotation direction
  const arcRadius = 20;
  ctx.strokeStyle = '#0066CC';
  ctx.lineWidth = 2;

  // Draw reference line at 0 degrees (pointing right)
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(arcRadius + 5, 0);
  ctx.stroke();

  // Draw arc showing positive angle direction (0 to -45 degrees = CCW)
  // Objects actually rotate CCW for positive angles
  ctx.beginPath();
  ctx.arc(0, 0, arcRadius, 0, -Math.PI / 4, true); // true = counterclockwise
  ctx.stroke();

  // Draw arrow at end of arc (at -45 degrees for CCW)
  const arrowAngle = -Math.PI / 4;
  const arrowX = Math.cos(arrowAngle) * arcRadius;
  const arrowY = Math.sin(arrowAngle) * arcRadius;

  // Calculate arrow direction (tangent to the circle at this point for CCW)
  // For CCW at -45°, tangent points in direction (-sin(-45°), cos(-45°))
  const tangentX = Math.sin(arrowAngle); // Points left-up for CCW
  const tangentY = -Math.cos(arrowAngle);

  // Draw a proper arrowhead
  ctx.beginPath();
  // Main arrow line from arc endpoint
  ctx.moveTo(arrowX, arrowY);

  // Calculate the two wings of the arrowhead
  // Wing 1: back and to the right (relative to arrow direction)
  const wing1X = arrowX - tangentX * arrowSize - tangentY * arrowSize * 0.5;
  const wing1Y = arrowY - tangentY * arrowSize + tangentX * arrowSize * 0.5;

  // Wing 2: back and to the left (relative to arrow direction)
  const wing2X = arrowX - tangentX * arrowSize + tangentY * arrowSize * 0.5;
  const wing2Y = arrowY - tangentY * arrowSize - tangentX * arrowSize * 0.5;

  // Draw the arrow wings
  ctx.lineTo(wing1X, wing1Y);
  ctx.moveTo(arrowX, arrowY);
  ctx.lineTo(wing2X, wing2Y);
  ctx.stroke();

  // Add labels
  ctx.fillStyle = '#0066CC';
  ctx.font = 'bold 10px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('0°', arcRadius + 12, 0);
  ctx.fillText('+45°', arrowX + 10, arrowY - 10);

  // Add "CCW" label to clarify
  ctx.font = '9px Arial';
  ctx.fillText('(CCW)', 0, 12);

  // Title
  ctx.fillStyle = '#333333';
  ctx.font = 'bold 11px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillText('Angle Direction', 0, -25);

  // === Now draw the axes below ===
  // Back to screen space (dropping the translate above) without a second
  // save(), so the single restore() at the end still balances.
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.translate(centerX, centerY);

  // Draw a semi-transparent background for axes
  ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
  ctx.fillRect(-10, -10, axisLength + 30, axisLength + 30);

  // Draw a border
  ctx.strokeStyle = '#333333';
  ctx.lineWidth = 1;
  ctx.strokeRect(-10, -10, axisLength + 30, axisLength + 30);

  // Draw origin point
  ctx.fillStyle = '#FF0000';
  ctx.beginPath();
  ctx.arc(0, 0, 3, 0, Math.PI * 2);
  ctx.fill();

  // Draw X axis (red)
  ctx.strokeStyle = '#FF0000';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(axisLength, 0);
  ctx.stroke();

  // X axis arrow
  ctx.beginPath();
  ctx.moveTo(axisLength, 0);
  ctx.lineTo(axisLength - arrowSize, -arrowSize / 2);
  ctx.moveTo(axisLength, 0);
  ctx.lineTo(axisLength - arrowSize, arrowSize / 2);
  ctx.stroke();

  // X axis label
  ctx.fillStyle = '#FF0000';
  ctx.font = 'bold 12px Arial';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('X', axisLength + 5, 0);

  // Draw Y axis (green) - Note: In 2D floor plan, Y is actually Z
  ctx.strokeStyle = '#00FF00';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(0, axisLength);
  ctx.stroke();

  // Y axis arrow
  ctx.beginPath();
  ctx.moveTo(0, axisLength);
  ctx.lineTo(-arrowSize / 2, axisLength - arrowSize);
  ctx.moveTo(0, axisLength);
  ctx.lineTo(arrowSize / 2, axisLength - arrowSize);
  ctx.stroke();

  // Y axis label (showing it's actually Z)
  ctx.fillStyle = '#00FF00';
  ctx.font = 'bold 12px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText('Z', 0, axisLength + 5);

  ctx.restore();
};

/**
 * Draws room labels
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Array} rooms - Array of room objects
 * @param {Object} data - Full structure data
 * @param {boolean} debugMode - Whether to show debug info (room index and ID suffix)
 */
export const drawRoomLabels = (ctx, rooms, data, debugMode = false) => {
  if (!rooms) return;

  rooms.forEach((room, roomIndex) => {
    // A satellite (a closet / pantry / nook the pipeline attached to a
    // larger room — see `utils/roomGroupUtils`) has no name of its own and
    // reads as part of its host. Without this skip the fallback chain
    // below prints `getRoomType`'s "Unknown" on top of the closet.
    if (isSatelliteRoom(room)) return;

    // What a label says when nothing names it: the room as `getRoomType`
    // reads it (its first labelled section, else "Unknown").
    const unnamed = getRoomType(room, data);

    const addDebugInfo = (label) => {
      if (!debugMode) return label;
      const roomId = room.id;
      const idSuffix = roomId.length >= 4 ? roomId.slice(-4) : roomId;
      return `${roomIndex} ${label}${idSuffix ? '-' + idSuffix : ''}`;
    };

    // Shared anchor policy (see `shared-lib/geometry/roomLabelAnchor.js`):
    //   - no sections                        → 1 label at room.center
    //   - single section close to room.center → 1 label at room.center
    //   - single section far from room.center → 1 label at section.center
    //   - multiple sections                  → 1 label per section at
    //                                          section.center
    const anchors = chooseRoomLabelAnchors(room);

    for (const { point, section } of anchors) {
      // The label rule the minimap and the 3D labels share (`roomLabelText`)
      const labelText = addDebugInfo(
        roomAnchorLabelText(room, section, {
          unnamedSection: unnamed,
          unnamedRoom: unnamed,
        })
      );

      drawLabel(ctx, labelText, point.x, point.z, {
        fontSize: 12,
        fontFamily: 'Arial',
        color: '#333333',
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        padding: 4,
        counterRotate: true,
      });
    }
  });
};
